// src/envelope.ts
import { z } from "zod";
var Classification = z.enum(["public", "internal", "confidential", "restricted"]);
var ActorRef = z.object({
  actor_type: z.enum(["agent", "human", "service", "system"]),
  actor_id: z.string().min(1),
  /** Nome do agente lógico (01..19) quando actor_type === 'agent'. */
  agent: z.string().min(1).nullable().default(null)
});
var ObjectRef = z.object({
  object_type: z.string().min(1),
  object_id: z.string().min(1),
  revision: z.number().int().nonnegative().nullable().default(null)
});
var SourceReference = z.object({
  source_asset_id: z.string().min(1).nullable().default(null),
  uri: z.string().min(1).nullable().default(null),
  title: z.string().nullable().default(null),
  publisher: z.string().nullable().default(null),
  published_at: z.string().datetime({ offset: true }).nullable().default(null),
  captured_at: z.string().datetime({ offset: true }).nullable().default(null),
  /** Trecho citado. Nunca substitui a verificação do Fact Checker. */
  excerpt: z.string().nullable().default(null),
  authority_score: z.number().min(0).max(100).nullable().default(null)
});
var Money = z.object({
  amount_minor: z.number().int(),
  currency: z.string().length(3).regex(/^[A-Z]{3}$/)
});
var TimeWindow = z.object({
  start_at: z.string().datetime({ offset: true }),
  end_at: z.string().datetime({ offset: true }).nullable().default(null)
}).refine((w) => w.end_at === null || Date.parse(w.end_at) >= Date.parse(w.start_at), {
  message: "end_at deve ser posterior ou igual a start_at"
});
var KillCriterion = z.object({
  criterion: z.string().min(1),
  /** Como o critério é avaliado deterministicamente. Sem isto, kill criterion é decoração. */
  evaluation: z.string().min(1),
  expires_at: z.string().datetime({ offset: true }).nullable().default(null)
});
var OverrideRecord = z.object({
  overridden_ref: ObjectRef,
  /** Override de evidência moderate/strong EXIGE motivo (Workflow §4). */
  reason: z.string().min(1),
  evidence_level_overridden: z.enum(["weak", "moderate", "strong"]),
  actor: ActorRef,
  occurred_at: z.string().datetime({ offset: true })
});
var Permission = z.object({
  permission_id: z.string().min(1),
  granted_by: z.string().min(1),
  /** Classe EXATA de conteúdo autorizada. Permissão genérica é recusada. */
  authorized_content_classes: z.array(z.string().min(1)).min(1),
  window: TimeWindow,
  conditions: z.array(z.string()).default([])
});
var materialObjectFields = {
  id: z.string().min(1),
  schema_version: z.string().min(1),
  revision: z.number().int().nonnegative(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  created_by: ActorRef,
  correlation_id: z.string().min(1),
  causation_id: z.string().min(1).nullable().default(null),
  source_refs: z.array(SourceReference).default([]),
  confidence: z.number().min(0).max(1).nullable().default(null),
  classification: Classification,
  metadata: z.record(z.unknown()).default({})
};
var MaterialObjectEnvelope = z.object(materialObjectFields);
function materialObject(shape) {
  return z.object({ ...materialObjectFields, ...shape });
}

// src/enums.ts
import { z as z2 } from "zod";
var AgentId = z2.enum([
  "radar",
  // 01
  "loyalty_cast_intelligence",
  // 02
  "sergio_digital_twin",
  // 03
  "editor_chefe",
  // 04
  "creative_and_hook",
  // 05
  "production",
  // 06
  "fact_checker",
  // 07
  "brand_guardian",
  // 08
  "legal_and_compliance",
  // 09
  "safety_guardian",
  // 10
  "voice_and_authenticity",
  // 11
  "publisher",
  // 12
  "growth",
  // 13
  "community",
  // 14
  "audience_intelligence",
  // 15
  "audience_acquisition",
  // 16
  "learning_and_recommendation",
  // 17
  "strategic_agenda",
  // 18
  "inbound_sales_and_qualification"
  // 19
]);
var LogicalDomain = z2.enum([
  "discovery_and_sources",
  "editorial_strategy",
  "creative_studio",
  "governance",
  "publishing",
  "growth",
  "audience_and_community",
  "learning",
  "commercial"
]);
var ContentFormat = z2.enum([
  "reel",
  "story",
  "carousel",
  "image",
  "digital_twin",
  "loyalty_cut"
]);
var ContentObjective = z2.enum([
  "acquisition",
  "authority",
  "education",
  "engagement",
  "youtube_traffic",
  "strategic",
  "commercial",
  "community"
]);
var PortfolioBucket = z2.enum(["core_proven", "growth", "experimentation"]);
var AutonomyLevel = z2.union([
  z2.literal(0),
  z2.literal(1),
  z2.literal(2),
  z2.literal(3),
  z2.literal(4)
]);
var AUTONOMY_LEVEL_LABELS = {
  0: "Manual",
  1: "Suggest only",
  2: "Auto low-risk",
  3: "Auto normal-risk",
  4: "High autonomy within policies"
};
var FreshnessClass = z2.enum([
  "OPERATIONAL_FINANCIAL",
  "EARLY_CONTENT",
  "MATURE_ANALYTICS",
  "LEARNING_STRATEGIC"
]);
var FreshnessStatus = z2.enum(["FRESH", "AGING", "STALE"]);
var CapabilityStatus = z2.enum([
  "SUPPORTED",
  "UNSUPPORTED",
  "NOT_TESTED",
  "ACCOUNT_RESTRICTED",
  "PERMISSION_MISSING",
  "DEPRECATED",
  "TEMPORARILY_UNAVAILABLE",
  "UNKNOWN"
]);
var CAPABILITY_STATUS_UNUSABLE = [
  "UNSUPPORTED",
  "NOT_TESTED",
  "ACCOUNT_RESTRICTED",
  "PERMISSION_MISSING",
  "DEPRECATED",
  "TEMPORARILY_UNAVAILABLE",
  "UNKNOWN"
];
var ModerationVisibility = z2.enum(["KEEP_VISIBLE", "HIDE", "DELETE"]);
var ModerationAction = z2.enum([
  "NO_ACTION",
  "LOG_ONLY",
  "RESPOND",
  "ROUTE",
  "REPORT",
  "ESCALATE",
  "URGENT_ESCALATION"
]);
var ModerationResponse = z2.object({
  visibility: ModerationVisibility,
  actions: z2.array(ModerationAction)
});
var MODERATION_ACTIONS_REQUIRING_HUMAN = [
  "ESCALATE",
  "URGENT_ESCALATION"
];
var MODERATION_COMPOSITES_REMOVED = {
  HIDE_AND_REPORT: { visibility: "HIDE", actions: ["REPORT"] },
  ROUTE_AND_KEEP_VISIBLE: { visibility: "KEEP_VISIBLE", actions: ["ROUTE"] }
};
var CapabilityAlertSeverity = z2.enum(["INFO", "WARNING", "CRITICAL"]);
var CapabilityChangeKind = z2.enum([
  "NON_MATERIAL_DOC_CHANGE",
  "CAPABILITY_ADDED",
  "CAPABILITY_REMOVED",
  "CAPABILITY_DEPRECATED",
  "SEMANTICS_CHANGED",
  "LIMIT_CHANGED",
  "PERMISSION_CHANGED",
  "FIELD_ADDED",
  "FIELD_REMOVED",
  "FIELD_BEHAVIOR_CHANGED",
  "RUNTIME_BREAKAGE",
  "AUTH_REQUIREMENT_CHANGED",
  "UNKNOWN_REQUIRES_REVIEW"
]);
var CAPABILITY_CHANGES_NAO_MATERIAIS = [
  "NON_MATERIAL_DOC_CHANGE"
];
var InteractionClassification = z2.enum([
  /** Ainda não classificada. Falha FECHADO: nada automático acontece. */
  "unclassified",
  /** Classificada e sem nada de especial. Diferente de `unclassified`. */
  "normal",
  "question",
  "praise",
  "confusion",
  "support_request",
  "commercial_intent",
  // A taxonomia de moderação, na MESMA escala: a política padrão é lida POR
  // categoria, então classificar e moderar têm de falar o mesmo vocabulário.
  // Duas listas para o mesmo conceito é o defeito que estava aqui antes.
  //
  // São as CATORZE classes das Regras de Operação §3.2, não as dez do Console
  // §85. Os dois documentos do cliente descrevem eixos diferentes: o §85 diz
  // quais classes têm ação padrão CONFIGURÁVEL, e o §3.2 diz quais classes
  // existem. Ficar só com as dez deixaria `hostile`, `negative_feedback`,
  // `illegal_content` e `platform_material_violation` sem como serem
  // expressas, e as regras de §3.4 e §3.5 para elas seriam inalcançáveis.
  "constructive_criticism",
  "professional_disagreement",
  "negative_feedback",
  "customer_complaint",
  /** Hostil sem chegar a ataque pessoal nem a reincidência. §3.4 permite ocultar. */
  "hostile",
  "personal_attack",
  "repeated_hostility",
  "spam",
  "scam",
  "impersonation",
  "threat",
  /** `hate_default_action` no §85; `HATE_OR_DISCRIMINATION` no §3.2. Mesma coisa. */
  "hate",
  "illegal_content",
  "platform_material_violation"
]);
var CLASSIFICATIONS_NEVER_HIDDEN = [
  "constructive_criticism",
  "professional_disagreement",
  /**
   * Regras de Operação §3.3 lista "feedback negativo legítimo" entre o que
   * permanece visível por padrão, ao lado de crítica e discordância. Deixá-lo
   * fora seria o mesmo defeito por outro nome: silenciar quem reclama com
   * fundamento porque a etiqueta é outra.
   */
  "negative_feedback"
];
var CLASSIFICATIONS_REPORTABLE = [
  "scam",
  "impersonation",
  "threat",
  "hate",
  "illegal_content",
  "platform_material_violation"
];
var ModerationCategory = z2.enum([
  "constructive_criticism",
  "professional_disagreement",
  "spam",
  "scam",
  "personal_attack",
  "repeated_hostility",
  "threat",
  "hate",
  "impersonation",
  "customer_complaint"
]);
var Timeliness = z2.enum(["breaking", "timely", "scheduled", "evergreen"]);
var EvidenceLevel = z2.enum(["weak", "moderate", "strong"]);
var Severity = z2.enum(["info", "low", "medium", "high", "critical"]);
var RiskLevel = z2.enum(["low", "medium", "high", "exceptional"]);
var GovernanceReviewer = z2.enum(["fact", "brand", "legal", "safety", "voice"]);
var GovernanceResult = z2.enum([
  "approve",
  "approve_with_conditions",
  "revise",
  "block",
  "escalate",
  "not_required"
]);
var GateType = z2.enum([
  "preproduction",
  "cost",
  "release",
  "technical_qa",
  "response",
  "publication"
]);
var GateOutcome = z2.enum(["pass", "deny", "hold", "revise", "escalate"]);
var ReleaseStatus = z2.enum(["embargo", "scheduled", "public", "reusable", "revoked"]);
var ConfigurationClass = z2.enum([
  "constitutional",
  "hard_limit",
  "operating_policy",
  "learning_allowlisted",
  "experimental",
  "capability"
]);
var DeployEnvironment = z2.enum(["development", "sandbox", "staging", "production"]);
var PauseScope = z2.enum([
  "system",
  "publication",
  "paid_media",
  "digital_twin",
  "community_auto_send",
  "sales_auto_send",
  "topic",
  "agenda",
  "integration"
]);
var CostCategory = z2.enum(["heygen", "paid_media", "model", "production"]);
var SnapshotAge = z2.enum(["1h", "6h", "24h", "72h", "7d", "custom"]);

// src/editorial.ts
import { z as z3 } from "zod";
var AudienceRef = z3.object({
  cohort_id: z3.string().min(1).nullable().default(null),
  persona: z3.string().nullable().default(null),
  industry: z3.array(z3.string()).default([]),
  seniority: z3.array(z3.string()).default([]),
  /** UNKNOWN é um valor legítimo e o default. Nunca inventar seniority/tier (AT-100). */
  tier: z3.enum(["A", "B", "C", "UNKNOWN"]).default("UNKNOWN")
});
var ContentOpportunity = materialObject({
  opportunity_id: z3.string().min(1),
  source_type: z3.enum([
    "radar",
    "loyalty_cast",
    "community",
    "strategic_agenda",
    "learning",
    "evergreen",
    "owner"
  ]),
  source_id: z3.string().min(1).nullable().default(null),
  topic: z3.string().min(1),
  subtopics: z3.array(z3.string()).default([]),
  summary: z3.string().min(1),
  why_it_matters: z3.string().min(1),
  insight: z3.string().min(1),
  target_audiences: z3.array(AudienceRef).default([]),
  target_industries: z3.array(z3.string()).default([]),
  timeliness: Timeliness,
  possible_formats: z3.array(ContentFormat).default([]),
  possible_hooks: z3.array(z3.string()).default([]),
  evidence_refs: z3.array(SourceReference).default([]),
  scores: z3.object({
    source_authority: z3.number().min(0).max(100),
    icp_relevance: z3.number().min(0).max(100),
    novelty: z3.number().min(0).max(100),
    timing: z3.number().min(0).max(100),
    transferability: z3.number().min(0).max(100).nullable().default(null),
    opportunity: z3.number().min(0).max(100)
  }),
  strategic_agenda_ids: z3.array(z3.string()).default([]),
  release_policy_id: z3.string().min(1).nullable().default(null),
  status: z3.enum([
    "discovered",
    "under_review",
    "selected",
    "deferred",
    "evergreen",
    "rejected",
    "expired"
  ]),
  expires_at: z3.string().datetime({ offset: true }).nullable().default(null)
}).superRefine((o, ctx) => {
  if (o.timeliness === "breaking" && o.expires_at === null) {
    ctx.addIssue({
      code: z3.ZodIssueCode.custom,
      path: ["expires_at"],
      message: "AT-024: oportunidade breaking (Fast Lane) exige expires_at explicito"
    });
  }
});
var EditorialDecision = materialObject({
  editorial_decision_id: z3.string().min(1),
  opportunity_id: z3.string().min(1),
  decision: z3.enum(["select", "defer", "reject", "request_research", "repurpose"]),
  objective: ContentObjective,
  target_audiences: z3.array(AudienceRef).default([]),
  format: ContentFormat,
  priority: z3.number().min(0).max(100),
  publication_window: TimeWindow.nullable().default(null),
  portfolio_bucket: PortfolioBucket,
  reason: z3.string().min(1),
  learning_recommendation_refs: z3.array(z3.string()).default([]),
  strategic_agenda_ids: z3.array(z3.string()).default([]),
  /** Override de recomendacao moderate/strong EXIGE OverrideRecord com motivo (AT-013). */
  override: OverrideRecord.nullable().default(null),
  kill_criteria: z3.array(KillCriterion).default([]),
  status: z3.enum(["recorded", "superseded"])
});
var ContentState = z3.enum([
  "DISCOVERED",
  "UNDER_EDITORIAL_REVIEW",
  "SELECTED",
  "DEFERRED",
  "REJECTED",
  "EVERGREEN",
  "CREATIVE_BRIEF",
  "SCRIPTING",
  "PREPRODUCTION_GOVERNANCE",
  "REVISION",
  "BLOCKED",
  "ESCALATED",
  "SEMANTICALLY_APPROVED",
  "WAITING_PRODUCTION",
  "COST_CHECK",
  "PRODUCING",
  "TECHNICAL_QA",
  "READY_TO_PUBLISH",
  "RELEASE_CHECK",
  "QUEUED",
  "SCHEDULED",
  "PUBLISHING",
  "PUBLISHED",
  "PUBLISH_FAILED",
  "HELD",
  "CANCELLED",
  "MEASURING",
  "LEARNED",
  "REPURPOSE_REQUESTED",
  "RETIRED"
]);
var ContentItem = materialObject({
  content_id: z3.string().min(1),
  opportunity_id: z3.string().min(1),
  parent_content_id: z3.string().min(1).nullable().default(null),
  topic: z3.string().min(1),
  subtopics: z3.array(z3.string()).default([]),
  industry: z3.array(z3.string()).default([]),
  target_audiences: z3.array(AudienceRef).default([]),
  format: ContentFormat,
  objective: ContentObjective,
  strategic_agenda_ids: z3.array(z3.string()).default([]),
  campaign_stage: z3.string().nullable().default(null),
  editorial_decision_id: z3.string().min(1),
  creative_brief_id: z3.string().min(1).nullable().default(null),
  current_script_version_id: z3.string().min(1).nullable().default(null),
  governance_bundle_id: z3.string().min(1).nullable().default(null),
  production_asset_ids: z3.array(z3.string()).default([]),
  publication_record_ids: z3.array(z3.string()).default([]),
  experiment_id: z3.string().min(1).nullable().default(null),
  lifecycle_state: ContentState,
  /** Detecta repeticao CONCEITUAL, nao so arquivo duplicado (Architecture §7, AT-012). */
  semantic_fingerprint: z3.string().min(1),
  valid_until: z3.string().datetime({ offset: true }).nullable().default(null),
  status: ContentState
});
var Hypothesis = z3.object({
  label: z3.string().min(1),
  hook: z3.string().min(1),
  rationale: z3.string().min(1)
});
var CreativeBrief = materialObject({
  creative_brief_id: z3.string().min(1),
  content_id: z3.string().min(1),
  message: z3.string().min(1),
  hook_family: z3.string().min(1),
  hook_hypotheses: z3.array(Hypothesis).default([]),
  narrative_structure: z3.string().min(1),
  shortest_sufficient_duration_seconds: z3.number().int().positive().nullable().default(null),
  cta_type: z3.enum(["none", "light", "specific", "commercial", "youtube"]),
  cta_text: z3.string().nullable().default(null),
  /** AT-046: B-roll sem funcao declarada e removido. Array vazio = sem B-roll. */
  broll_purpose: z3.array(z3.string().min(1)).default([]),
  visual_intent: z3.string().min(1),
  experiment_variable: z3.string().nullable().default(null),
  success_metric: z3.string().min(1),
  status: z3.enum(["draft", "active", "superseded"])
});
var TimedText = z3.object({
  at_ms: z3.number().int().nonnegative(),
  text: z3.string().min(1)
});
var ClaimRef = z3.object({
  claim_id: z3.string().min(1),
  surface: z3.enum(["script", "caption", "cover", "on_screen", "story", "comment", "dm"])
});
var ScriptVersion = materialObject({
  script_version_id: z3.string().min(1),
  content_id: z3.string().min(1),
  version: z3.number().int().positive(),
  script: z3.string().min(1),
  caption: z3.string(),
  cover_text: z3.string().nullable().default(null),
  on_screen_text: z3.array(TimedText).default([]),
  claims: z3.array(ClaimRef).default([]),
  first_person: z3.boolean(),
  generated_with_ai: z3.boolean(),
  change_reason: z3.string().min(1),
  status: z3.enum(["draft", "governance_review", "revise", "approved", "superseded"])
});
var SourceAsset = materialObject({
  source_asset_id: z3.string().min(1),
  source_type: z3.enum([
    "video",
    "transcript",
    "article",
    "report",
    "dataset",
    "webpage",
    "owner_note",
    "product_record"
  ]),
  uri: z3.string().min(1),
  title: z3.string().min(1),
  author: z3.string().nullable().default(null),
  publisher: z3.string().nullable().default(null),
  published_at: z3.string().datetime({ offset: true }).nullable().default(null),
  captured_at: z3.string().datetime({ offset: true }),
  content_hash: z3.string().min(1),
  language: z3.string().min(2),
  authority_score: z3.number().min(0).max(100).nullable().default(null),
  valid_until: z3.string().datetime({ offset: true }).nullable().default(null),
  release_status: ReleaseStatus,
  rights_policy_id: z3.string().min(1).nullable().default(null),
  status: z3.enum(["ingested", "indexed", "stale", "revoked"])
});
var Guest = z3.object({
  name: z3.string().min(1),
  instagram_handle: z3.string().nullable().default(null),
  role: z3.string().nullable().default(null)
});
var Company = z3.object({
  name: z3.string().min(1),
  instagram_handle: z3.string().nullable().default(null),
  industry: z3.string().nullable().default(null)
});
var LoyaltyEpisode = materialObject({
  episode_id: z3.string().min(1),
  original_asset_id: z3.string().min(1),
  youtube_video_id: z3.string().min(1).nullable().default(null),
  guest: Guest,
  company: Company,
  recording_date: z3.string().date().nullable().default(null),
  scheduled_publish_at: z3.string().datetime({ offset: true }).nullable().default(null),
  /**
   * D-012: status publico do YouTube e a autoridade default de release.
   * Status DESCONHECIDO falha fechado: modelado como `recorded_embargo`.
   */
  release_status: z3.enum(["recorded_embargo", "scheduled", "public", "reusable", "hold"]),
  prelaunch_permission: Permission.nullable().default(null),
  transcript_id: z3.string().min(1),
  rights_policy_id: z3.string().min(1),
  status: z3.enum([
    "NEW_SOURCE",
    "TRANSCRIBING",
    "MAPPING",
    "OPPORTUNITIES_READY",
    "RECORDED_EMBARGO",
    "SCHEDULED",
    "PUBLIC",
    "REUSABLE",
    "HOLD",
    "REVOKED"
  ])
});
var CutOpportunity = materialObject({
  cut_id: z3.string().min(1),
  episode_id: z3.string().min(1),
  start_ms: z3.number().int().nonnegative(),
  end_ms: z3.number().int().positive(),
  speaker: z3.string().min(1),
  category: z3.enum([
    "insight",
    "story",
    "number",
    "error",
    "lesson",
    "contrarian",
    "prediction",
    "quote",
    "question"
  ]),
  summary: z3.string().min(1),
  /** Contexto antes/depois e obrigatorio: e o que permite provar integridade (AT-032). */
  context_before: z3.string(),
  context_after: z3.string(),
  cut_score: z3.number().min(0).max(100),
  context_integrity_score: z3.number().min(0).max(100),
  amplification_potential_score: z3.number().min(0).max(100),
  shelf_life: z3.enum(["breaking", "timely", "evergreen"]),
  status: z3.enum(["proposed", "selected", "rejected", "used"])
}).refine((c) => c.end_ms > c.start_ms, {
  message: "end_ms deve ser maior que start_ms",
  path: ["end_ms"]
});

// src/governance.ts
import { z as z4 } from "zod";
var Claim = materialObject({
  claim_id: z4.string().min(1),
  content_id: z4.string().min(1),
  script_version_id: z4.string().min(1),
  /** Toda superficie publica e verificada, nao so o script (AT-061: caption certa, capa falsa). */
  surface: z4.enum(["script", "caption", "cover", "on_screen", "story", "comment", "dm"]),
  claim_text: z4.string().min(1),
  claim_type: z4.enum([
    "number",
    "date",
    "person",
    "company",
    "market",
    "product",
    "quote",
    "comparison",
    "regulatory",
    "commercial"
  ]),
  materiality: z4.enum(["low", "medium", "high"]),
  source_refs: z4.array(SourceReference).default([]),
  source_reliability_score: z4.number().min(0).max(100),
  /**
   * `pending` nao e aprovacao e `unsupported` nao e "sem dados": sao estados
   * distintos, e a ausencia de verificacao NUNCA vira verificado.
   */
  verification_status: z4.enum([
    "pending",
    "verified",
    "qualified",
    "disputed",
    "unsupported",
    "stale"
  ]),
  verified_value: z4.string().nullable().default(null),
  qualification: z4.string().nullable().default(null),
  valid_until: z4.string().datetime({ offset: true }).nullable().default(null),
  checked_at: z4.string().datetime({ offset: true }).nullable().default(null),
  status: z4.enum(["open", "closed"])
}).superRefine((c, ctx) => {
  if (c.verification_status === "verified" && c.source_refs.length === 0) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["verification_status"],
      message: "AT-060/AT-021: claim verified exige ao menos uma SourceReference"
    });
  }
  if (c.verification_status === "verified" && c.checked_at === null) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["checked_at"],
      message: "claim verified exige checked_at"
    });
  }
});
var ReviewIssue = z4.object({
  /** Codigo estavel: e o que permite roteamento estruturado no loop de revisao. */
  code: z4.string().min(1),
  surface: z4.enum(["script", "caption", "cover", "on_screen", "story", "comment", "dm", "asset"]),
  excerpt: z4.string().nullable().default(null),
  required_change: z4.string().min(1),
  severity: z4.enum(["info", "low", "medium", "high", "critical"])
});
var GovernanceReview = materialObject({
  governance_review_id: z4.string().min(1),
  content_id: z4.string().min(1),
  /** A review aponta para a versao EXATA. Nova versao => review refeita (Workflow §5). */
  script_version_id: z4.string().min(1),
  reviewer: GovernanceReviewer,
  policy_version: z4.string().min(1),
  result: GovernanceResult,
  issues: z4.array(ReviewIssue).default([]),
  evidence_refs: z4.array(SourceReference).default([]),
  score: z4.number().min(0).max(100).nullable().default(null),
  reason: z4.string().min(1),
  status: z4.enum(["recorded"])
}).superRefine((r, ctx) => {
  if (r.result === "revise" && r.issues.length === 0) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["issues"],
      message: "result=revise exige issues estruturadas para rotear ao Creative"
    });
  }
  if (r.reviewer === "voice" && r.result !== "not_required" && r.score === null) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["score"],
      message: "review de voice exige score de Voice Authenticity"
    });
  }
});
var GovernanceBundle = materialObject({
  governance_bundle_id: z4.string().min(1),
  content_id: z4.string().min(1),
  script_version_id: z4.string().min(1),
  required_reviews: z4.array(GovernanceReviewer).min(1),
  review_ids: z4.array(z4.string().min(1)).default([]),
  status: z4.enum(["pending", "approved", "revise", "blocked", "escalated"]),
  approved_at: z4.string().datetime({ offset: true }).nullable().default(null),
  invalidated_reason: z4.string().nullable().default(null)
}).superRefine((b, ctx) => {
  if (b.status === "approved" && b.approved_at === null) {
    ctx.addIssue({
      code: z4.ZodIssueCode.custom,
      path: ["approved_at"],
      message: "bundle approved exige approved_at"
    });
  }
});
var GateResult = materialObject({
  gate_result_id: z4.string().min(1),
  gate_type: GateType,
  object_type: z4.string().min(1),
  object_id: z4.string().min(1),
  policy_version: z4.string().min(1),
  result: GateOutcome,
  /** Codigo estavel de motivo. Mensagem livre nao e contrato. */
  reason_code: z4.string().min(1),
  reason: z4.string().min(1),
  expires_at: z4.string().datetime({ offset: true }).nullable().default(null),
  status: z4.enum(["recorded"])
});

// src/production.ts
import { z as z5 } from "zod";
var ProductionRequest = materialObject({
  production_request_id: z5.string().min(1),
  content_id: z5.string().min(1),
  /** Somente versao APROVADA entra em producao (AT-047, agente 06). */
  approved_script_version_id: z5.string().min(1),
  production_type: z5.enum(["standard", "loyalty_cut", "digital_twin"]),
  /** Gates que precisam estar `pass` para o pedido ser executavel (AT-050). */
  required_gate_result_ids: z5.array(z5.string().min(1)).min(1),
  requested_at: z5.string().datetime({ offset: true }),
  not_before: z5.string().datetime({ offset: true }).nullable().default(null),
  status: z5.enum(["requested", "reserved", "executing", "completed", "failed", "cancelled"])
});
var ProductionAsset = materialObject({
  production_asset_id: z5.string().min(1),
  content_id: z5.string().min(1),
  type: ContentFormat,
  version: z5.number().int().positive(),
  file_ref: z5.string().min(1),
  duration_seconds: z5.number().nonnegative().nullable().default(null),
  resolution: z5.string().min(1),
  aspect_ratio: z5.string().min(1),
  /** QA tecnico: `pending` nunca conta como aprovado (AT-048). */
  subtitles_status: z5.enum(["pending", "pass", "fail"]),
  audio_status: z5.enum(["pending", "pass", "fail"]),
  visual_qa_status: z5.enum(["pending", "pass", "fail"]),
  generated_with_ai: z5.boolean(),
  provider_render_id: z5.string().min(1).nullable().default(null),
  production_cost: Money,
  /** Muda => volta a governanca semantica (AT-047). */
  semantic_hash: z5.string().min(1),
  status: z5.enum(["draft", "qa_pending", "qa_failed", "ready", "superseded"])
});
var SocialHandle = z5.object({
  platform: z5.literal("instagram"),
  handle: z5.string().min(1),
  /** Por que a tag existe: convidado, empresa, parceiro. Tag sem motivo nao entra. */
  role: z5.enum(["guest", "company", "partner", "other"])
});
var PublicationPackage = materialObject({
  package_id: z5.string().min(1),
  content_id: z5.string().min(1),
  asset_ids: z5.array(z5.string().min(1)).min(1),
  final_caption: z5.string(),
  cover_asset_id: z5.string().min(1).nullable().default(null),
  tags: z5.array(SocialHandle).default([]),
  hashtags: z5.array(z5.string()).default([]),
  story_link: z5.string().nullable().default(null),
  required_gate_result_ids: z5.array(z5.string().min(1)).min(1),
  release_policy_id: z5.string().min(1).nullable().default(null),
  /** Efeito publico at-most-once vem daqui + reconciliacao remota (Workflow §19). */
  idempotency_key: z5.string().min(1),
  status: z5.enum(["assembled", "queued", "consumed", "cancelled"])
});
var PublicationRecord = materialObject({
  publication_id: z5.string().min(1),
  package_id: z5.string().min(1),
  platform: z5.literal("instagram"),
  format: ContentFormat,
  scheduled_at: z5.string().datetime({ offset: true }).nullable().default(null),
  published_at: z5.string().datetime({ offset: true }).nullable().default(null),
  platform_post_id: z5.string().min(1).nullable().default(null),
  platform_url: z5.string().min(1).nullable().default(null),
  status: z5.enum([
    "queued",
    "scheduled",
    "publishing",
    "processing",
    "published",
    "failed",
    "retrying",
    "held",
    "cancelled"
  ]),
  retry_count: z5.number().int().nonnegative(),
  failure_code: z5.string().min(1).nullable().default(null),
  strategic_agenda_ids: z5.array(z5.string()).default([])
}).superRefine((p, ctx) => {
  if (p.status === "published" && p.platform_post_id === null) {
    ctx.addIssue({
      code: z5.ZodIssueCode.custom,
      path: ["platform_post_id"],
      message: "status=published exige platform_post_id confirmado pela plataforma"
    });
  }
  if (p.status === "published" && p.published_at === null) {
    ctx.addIssue({
      code: z5.ZodIssueCode.custom,
      path: ["published_at"],
      message: "status=published exige published_at"
    });
  }
});
var CostEnvelope = materialObject({
  envelope_id: z5.string().min(1),
  category: CostCategory,
  period: TimeWindow,
  money_limit: Money,
  quantity_limit: z5.number().int().nonnegative().nullable().default(null),
  seconds_limit: z5.number().int().nonnegative().nullable().default(null),
  approved_by: z5.string().min(1),
  status: z5.enum(["active", "exhausted", "paused", "superseded"])
});
var CostLedgerEntry = materialObject({
  ledger_entry_id: z5.string().min(1),
  envelope_id: z5.string().min(1),
  object_id: z5.string().min(1),
  amount: Money,
  quantity: z5.number().int().nullable().default(null),
  seconds: z5.number().int().nullable().default(null),
  entry_type: z5.enum(["reserve", "consume", "release", "adjust"]),
  /** Idempotencia do lancamento e CONSTRAINT no banco, nao `if` (playbook). */
  idempotency_key: z5.string().min(1),
  status: z5.enum(["recorded"])
});

// src/metrics.ts
import { z as z6 } from "zod";
var metric = () => z6.number().int().nonnegative().nullable().default(null);
var rate = () => z6.number().min(0).max(1).nullable().default(null);
var MetricSnapshot = materialObject({
  snapshot_id: z6.string().min(1),
  publication_id: z6.string().min(1),
  captured_at: z6.string().datetime({ offset: true }),
  age_since_publish: SnapshotAge,
  impressions: metric(),
  reach: metric(),
  non_follower_reach: metric(),
  views: metric(),
  watch_time_seconds: z6.number().nonnegative().nullable().default(null),
  average_watch_time_seconds: z6.number().nonnegative().nullable().default(null),
  completion_rate: rate(),
  skip_rate: rate(),
  likes: metric(),
  comments: metric(),
  shares: metric(),
  saves: metric(),
  profile_visits: metric(),
  follows: metric(),
  /** Estimado NUNCA e rotulado como observado (KPI Dictionary §1). */
  estimated_qualified_follows: z6.number().nonnegative().nullable().default(null),
  estimated_qualified_follows_method: z6.string().min(1).nullable().default(null),
  estimated_qualified_follows_confidence: z6.number().min(0).max(1).nullable().default(null),
  dms_generated: metric(),
  qualified_leads: metric(),
  meetings: metric(),
  paid_spend: Money.nullable().default(null),
  /** Snapshots sao IMUTAVEIS. Dado atrasado gera snapshot novo (Workflow §10, AT-085). */
  status: z6.enum(["captured"])
}).superRefine((s, ctx) => {
  if (s.estimated_qualified_follows !== null && s.estimated_qualified_follows_method === null) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["estimated_qualified_follows_method"],
      message: "AT-152: valor estimado exige metodo declarado"
    });
  }
  if (s.estimated_qualified_follows !== null && s.estimated_qualified_follows_confidence === null) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["estimated_qualified_follows_confidence"],
      message: "AT-152: valor estimado exige confianca declarada"
    });
  }
});
var Variant = z6.object({
  label: z6.string().min(1),
  description: z6.string().min(1),
  content_ids: z6.array(z6.string().min(1)).default([])
});
var Experiment = materialObject({
  experiment_id: z6.string().min(1),
  hypothesis: z6.string().min(1),
  /** UMA variavel. Experimento multivariavel e devolvido para redesenho (AT-041). */
  variable: z6.string().min(1),
  control: Variant,
  challenger: Variant,
  target_audiences: z6.array(AudienceRef).default([]),
  primary_metric: z6.string().min(1),
  secondary_metrics: z6.array(z6.string()).default([]),
  minimum_sample_policy: z6.string().min(1),
  start_at: z6.string().datetime({ offset: true }),
  end_at: z6.string().datetime({ offset: true }).nullable().default(null),
  status: z6.enum(["planned", "running", "evaluating", "completed", "inconclusive", "stopped"]),
  result: z6.string().nullable().default(null),
  confidence: z6.number().min(0).max(1).nullable().default(null),
  /** `inconclusive` nao e derrota nem vitoria (KPI-051). `none` = empate medido. */
  winner: z6.enum(["control", "challenger", "none"]).nullable().default(null),
  learning_generated: z6.array(z6.string()).default([])
});
var LearningSignal = materialObject({
  signal_id: z6.string().min(1),
  type: z6.enum([
    "momentum",
    "saturation",
    "audience_drift",
    "performance_drift",
    "affinity",
    "sequence",
    "anomaly",
    "opportunity",
    "agent_friction"
  ]),
  target_ref: ObjectRef,
  evidence_refs: z6.array(ObjectRef).default([]),
  sample_size: z6.number().int().nonnegative(),
  confidence: z6.number().min(0).max(1),
  evidence_level: EvidenceLevel,
  severity: z6.enum(["info", "low", "medium", "high"]),
  expires_at: z6.string().datetime({ offset: true }).nullable().default(null),
  status: z6.enum(["open", "consumed", "expired", "superseded"])
});
var Recommendation = materialObject({
  recommendation_id: z6.string().min(1),
  destination_agent: z6.string().min(1),
  action_type: z6.string().min(1),
  recommended_action: z6.record(z6.unknown()),
  evidence_refs: z6.array(ObjectRef).default([]),
  confidence: z6.number().min(0).max(1),
  evidence_level: EvidenceLevel,
  requires_reason_on_override: z6.boolean(),
  valid_until: z6.string().datetime({ offset: true }).nullable().default(null),
  status: z6.enum(["open", "accepted", "overridden", "rejected", "expired"])
}).superRefine((r, ctx) => {
  if (r.evidence_level !== "weak" && r.requires_reason_on_override !== true) {
    ctx.addIssue({
      code: z6.ZodIssueCode.custom,
      path: ["requires_reason_on_override"],
      message: "evidencia moderate/strong exige requires_reason_on_override=true (AT-013)"
    });
  }
});
var ParameterChange = materialObject({
  parameter_change_id: z6.string().min(1),
  parameter_key: z6.string().min(1),
  old_value: z6.unknown(),
  new_value: z6.unknown(),
  allowlist_policy: z6.string().min(1),
  recommendation_id: z6.string().min(1),
  applied_by: z6.string().min(1),
  rollback_value: z6.unknown(),
  status: z6.enum([
    "proposed",
    "validated",
    "applied",
    "monitored",
    "retained",
    "rolled_back",
    "rejected"
  ])
});
var AudienceCohort = materialObject({
  cohort_id: z6.string().min(1),
  name: z6.string().min(1),
  industry: z6.array(z6.string()).default([]),
  seniority: z6.array(z6.string()).default([]),
  function: z6.array(z6.string()).default([]),
  geography: z6.array(z6.string()).default([]),
  follower_status: z6.enum(["follower", "non_follower", "mixed", "unknown"]),
  behavioral_signals: z6.array(z6.string()).default([]),
  /** Demografico ausente do provedor nao vira coorte fabricada (AT-102). */
  size_estimate: z6.number().nonnegative().nullable().default(null),
  confidence: z6.number().min(0).max(1),
  audience_value: z6.number().min(0).max(100),
  status: z6.enum(["active", "superseded"])
});
var AudienceSignal = materialObject({
  signal_id: z6.string().min(1),
  /** Identificador de pessoa e RESTRICTED e nunca entra em dashboard agregado (AT-133). */
  audience_ref: z6.string().min(1).nullable().default(null),
  cohort_id: z6.string().min(1),
  signal_type: z6.enum([
    "view",
    "high_retention",
    "comment",
    "share",
    "follow",
    "profile_visit",
    "dm",
    "commercial_intent"
  ]),
  content_id: z6.string().min(1),
  strength: z6.number().min(0).max(1),
  confidence: z6.number().min(0).max(1),
  occurred_at: z6.string().datetime({ offset: true }),
  status: z6.enum(["recorded"])
});
var RelationshipEdge = materialObject({
  edge_id: z6.string().min(1),
  source: ObjectRef,
  target: ObjectRef,
  relationship: z6.enum(["affinity", "sequence", "predicts", "converts_to", "overlaps_with"]),
  weight: z6.number(),
  confidence: z6.number().min(0).max(1),
  sample_size: z6.number().int().nonnegative(),
  time_window: TimeWindow,
  status: z6.enum(["active", "superseded"])
});

// src/strategy.ts
import { z as z7 } from "zod";
var CampaignStage = z7.object({
  name: z7.string().min(1),
  /** Modelo narrativo default: problem -> education -> possibility -> solution -> conversion.
   *  Nao e obrigatorio nem linear: agenda pode ter modelo proprio (KPI-056). */
  order: z7.number().int().nonnegative(),
  objective: z7.string().min(1),
  desired_actions: z7.array(z7.string()).default([]),
  allowed_product_exposure: z7.string().min(1)
});
var StrategicAgenda = materialObject({
  agenda_id: z7.string().min(1),
  name: z7.string().min(1),
  objective: z7.string().min(1),
  business_objective: z7.string().min(1),
  target_audiences: z7.array(AudienceRef).default([]),
  start_at: z7.string().datetime({ offset: true }),
  end_at: z7.string().datetime({ offset: true }).nullable().default(null),
  priority: z7.number().int().positive(),
  /**
   * Share DESEJADO e direcional: nunca obriga a publicar conteudo fraco (AT-014).
   * Share MAXIMO protege o portfolio de ser monopolizado por uma agenda.
   */
  desired_content_share: z7.number().min(0).max(1),
  max_content_share: z7.number().min(0).max(1),
  campaign_stages: z7.array(CampaignStage).default([]),
  allowed_product_exposure: z7.string().min(1),
  desired_actions: z7.array(z7.string()).default([]),
  success_metrics: z7.array(z7.string()).default([]),
  kill_criteria: z7.array(KillCriterion).default([]),
  status: z7.enum(["draft", "active", "paused", "hold", "completed", "cancelled"]),
  /**
   * Agenda so nasce/muda a partir de instrucao VERIFICADA do dono (agente 18).
   * Sem esta referencia, nao existe agenda.
   */
  owner_instruction_ref: z7.string().min(1)
}).superRefine((a, ctx) => {
  if (a.max_content_share < a.desired_content_share) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: ["max_content_share"],
      message: "max_content_share nao pode ser menor que desired_content_share"
    });
  }
  const orders = a.campaign_stages.map((s) => s.order);
  if (new Set(orders).size !== orders.length) {
    ctx.addIssue({
      code: z7.ZodIssueCode.custom,
      path: ["campaign_stages"],
      message: "campaign_stages exige `order` unico por estagio"
    });
  }
});

// src/community.ts
import { z as z8 } from "zod";
var Interaction = materialObject({
  interaction_id: z8.string().min(1),
  platform: z8.literal("instagram"),
  type: z8.enum(["comment", "dm", "story_reply", "mention"]),
  content_id: z8.string().min(1).nullable().default(null),
  /** Referencia pseudonimizada. O identificador cru e RESTRICTED (AT-103, AT-133). */
  participant_ref: z8.string().min(1),
  /**
   * Texto do participante e conteudo NAO CONFIAVEL: nunca vira instrucao de sistema
   * (AT-008) e nunca aparece em dashboard agregado (AT-133).
   */
  text: z8.string(),
  classification: InteractionClassification,
  risk_level: RiskLevel,
  response_status: z8.enum(["none", "suggested", "approved", "sent", "held", "escalated"]),
  /**
   * Os DOIS eixos da decisão de moderação (decisão do dono, 2026-09-10).
   *
   * Um campo só, com enum composto, era o que fazia `ROUTE` ser
   * indistinguível de `NO_ACTION`: os dois deixavam o comentário visível e
   * o mesmo rastro. Encaminhar e manter visível são coisas independentes.
   */
  moderation_visibility: ModerationVisibility,
  moderation_actions: z8.array(ModerationAction).default([]),
  status: z8.enum([
    "NEW_INTERACTION",
    "CLASSIFIED",
    "MODERATION_POLICY",
    "RESPONSE_RISK",
    "SUGGESTED_RESPONSE",
    "REQUIRED_GOVERNANCE",
    "SENT",
    "HIDDEN",
    "HELD",
    "ESCALATED",
    "NO_ACTION"
  ])
}).superRefine((i, ctx) => {
  if (CLASSIFICATIONS_NEVER_HIDDEN.includes(i.classification) && i.moderation_visibility !== "KEEP_VISIBLE") {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["moderation_visibility"],
      message: "AT-111: critica legitima e discordancia profissional nao podem ser ocultadas por default; reclassifique como personal_attack, repeated_hostility ou spam com evidencia"
    });
  }
  if (CLASSIFICATIONS_NEVER_HIDDEN.includes(i.classification) && i.moderation_actions.includes("REPORT")) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["moderation_actions"],
      message: "SPEC \xA73.5: denuncia a plataforma e apenas para violacao material; critica e discordancia nao entram"
    });
  }
  if (i.risk_level === "exceptional" && i.response_status === "sent") {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["response_status"],
      message: "AT-114: risco exceptional exige revisao humana; envio automatico proibido"
    });
  }
});
var Conversation = materialObject({
  conversation_id: z8.string().min(1),
  participant_ref: z8.string().min(1),
  origin_content_id: z8.string().min(1).nullable().default(null),
  conversation_type: z8.enum(["community", "commercial", "customer_service"]),
  current_owner: z8.enum(["community", "sales", "human", "support"]),
  state: z8.string().min(1),
  interaction_ids: z8.array(z8.string().min(1)).default([]),
  commercial_intent_score: z8.number().min(0).max(100).nullable().default(null),
  last_interaction_at: z8.string().datetime({ offset: true }),
  /** Opt-out para follow-up: `true` para TODO envio imediatamente (AT-124). */
  opted_out: z8.boolean().default(false),
  status: z8.enum(["open", "closed"])
});
var PersonRef = z8.object({
  /** Nunca o identificador cru em projecao agregada. */
  person_ref: z8.string().min(1),
  display_role: z8.string().nullable().default(null)
});
var CompanyRef = z8.object({
  company_ref: z8.string().min(1),
  name: z8.string().nullable().default(null),
  industry: z8.string().nullable().default(null)
});
var Lead = materialObject({
  lead_id: z8.string().min(1),
  conversation_id: z8.string().min(1),
  person: PersonRef,
  company: CompanyRef,
  role: z8.string().nullable().default(null),
  industry: z8.string().nullable().default(null),
  problem: z8.string().min(1),
  product_interest: z8.array(z8.string()).default([]),
  /** ICP Fit e Buying Intent sao scores SEPARADOS e permanecem separados (KPI-060). */
  icp_score: z8.number().min(0).max(100).nullable().default(null),
  intent_score: z8.number().min(0).max(100).nullable().default(null),
  qualification_confidence: z8.number().min(0).max(1).nullable().default(null),
  current_stage: z8.enum([
    "discovery",
    "qualifying",
    "qualified",
    "meeting_offered",
    "follow_up",
    "meeting_booked",
    "nurture",
    "not_qualified",
    "closed"
  ]),
  next_best_action: z8.string().min(1),
  source_content_id: z8.string().min(1).nullable().default(null),
  strategic_agenda_ids: z8.array(z8.string()).default([]),
  status: z8.enum(["open", "closed"])
}).superRefine((l, ctx) => {
  if (l.current_stage === "not_qualified" && l.icp_score === null && l.intent_score === null) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["current_stage"],
      message: "AT-123: ausencia de evidencia nao desqualifica; use discovery/qualifying com scores null"
    });
  }
});
var FollowUpAction = materialObject({
  followup_id: z8.string().min(1),
  lead_id: z8.string().min(1),
  attempt_number: z8.number().int().positive(),
  /** Cada tentativa acrescenta valor NOVO. Sem motivo, nao envia (AT-125). */
  reason: z8.string().min(1),
  persuasion_strategy: z8.enum([
    "authority",
    "social_proof",
    "reciprocity",
    "affinity",
    "unity",
    "real_scarcity",
    "commitment",
    "loss_aversion"
  ]),
  planned_at: z8.string().datetime({ offset: true }),
  sent_at: z8.string().datetime({ offset: true }).nullable().default(null),
  message_version: z8.string().min(1),
  outcome: z8.string().nullable().default(null),
  /** Toda tentativa confere stop conditions ANTES de enviar (Workflow §13). */
  stop_condition_checked: z8.boolean(),
  status: z8.enum(["planned", "sent", "suppressed", "cancelled"])
}).superRefine((f, ctx) => {
  if (f.status === "sent" && f.stop_condition_checked !== true) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["stop_condition_checked"],
      message: "AT-124: envio exige stop_condition_checked=true antes do disparo"
    });
  }
  if (f.status === "sent" && f.sent_at === null) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      path: ["sent_at"],
      message: "status=sent exige sent_at"
    });
  }
});
var CommercialEvidence = materialObject({
  evidence_id: z8.string().min(1),
  product: z8.string().min(1),
  category: z8.string().min(1),
  approved_claim: z8.string().min(1),
  value: z8.string().min(1),
  metric: z8.string().min(1),
  context: z8.string().min(1),
  segment: z8.string().nullable().default(null),
  period: z8.string().nullable().default(null),
  source_ref: SourceReference,
  allowed_channels: z8.array(z8.enum(["public_content", "dm", "sales_meeting", "internal_only"])).min(1),
  conditions: z8.array(z8.string()).default([]),
  valid_from: z8.string().datetime({ offset: true }),
  valid_until: z8.string().datetime({ offset: true }).nullable().default(null),
  approval_status: z8.enum(["approved", "expired", "revoked"]),
  status: z8.enum(["current", "superseded"])
});

// src/knowledge.ts
import { z as z9 } from "zod";
var KnowledgeEntry = materialObject({
  knowledge_id: z9.string().min(1),
  domain: z9.enum([
    "sergio",
    "ock",
    "product",
    "brand",
    "commercial",
    "guest",
    "competitor",
    "editorial",
    "design",
    "platform"
  ]),
  statement: z9.string().min(1),
  type: z9.enum(["fact", "position", "rule", "preference", "procedure"]),
  classification: Classification,
  /**
   * Conhecimento DESCONHECIDO permanece desconhecido em producao
   * (configuration-and-required-inputs §4). Nao existe "current" por default.
   */
  status: z9.enum(["current", "superseded", "deprecated"]),
  approved_for_external_use: z9.boolean(),
  source_refs: z9.array(SourceReference).default([]),
  valid_from: z9.string().datetime({ offset: true }),
  valid_until: z9.string().datetime({ offset: true }).nullable().default(null)
});
var DecisionRecord = z9.object({
  decision_id: z9.string().min(1),
  decision_type: z9.string().min(1),
  agent: z9.string().min(1),
  input_refs: z9.array(ObjectRef).default([]),
  recommendation: z9.record(z9.unknown()).nullable().default(null),
  confidence: z9.number().min(0).max(1).nullable().default(null),
  /** Versao de policy/prompt/modelo que produziu a decisao. Sem isto nao ha auditoria. */
  policy_refs: z9.array(z9.string().min(1)).min(1),
  model_version: z9.string().min(1).nullable().default(null),
  prompt_version: z9.string().min(1).nullable().default(null),
  decision: z9.record(z9.unknown()),
  reason: z9.string().min(1),
  override: z9.boolean(),
  override_by: z9.string().min(1).nullable().default(null),
  occurred_at: z9.string().datetime({ offset: true }),
  correlation_id: z9.string().min(1),
  causation_id: z9.string().min(1).nullable().default(null)
}).superRefine((d, ctx) => {
  if (d.override === true && d.override_by === null) {
    ctx.addIssue({
      code: z9.ZodIssueCode.custom,
      path: ["override_by"],
      message: "override=true exige override_by identificado"
    });
  }
});
var DomainEvent = z9.object({
  event_id: z9.string().min(1),
  event_type: z9.string().min(1),
  object_type: z9.string().min(1),
  object_id: z9.string().min(1),
  produced_by: ActorRef,
  payload: z9.record(z9.unknown()),
  occurred_at: z9.string().datetime({ offset: true }),
  correlation_id: z9.string().min(1),
  causation_id: z9.string().min(1).nullable().default(null),
  idempotency_key: z9.string().min(1),
  schema_version: z9.string().min(1)
});

// src/admin.ts
import { z as z10 } from "zod";
var AdminCommandType = z10.enum([
  "PauseSystem",
  "ResumeSystem",
  "PauseCategory",
  "ResumeCategory",
  "HoldTopic",
  "ReleaseTopicHold",
  "HoldAgenda",
  "ReleaseAgendaHold",
  "CancelContent",
  "ChangeAgenda",
  "ChangeHardLimit",
  "ChangeOperatingPolicy",
  "GrantException",
  "RevokeException",
  "DecideEscalation",
  "ApproveParameterChange",
  "RejectParameterChange",
  "RollbackParameterChange",
  "AcknowledgeIncident",
  /**
   * Apagamento a pedido do titular (LGPD).
   *
   * O §15 declara esta lista FECHADA, e este valor NAO esta nele. Entrou porque
   * apagamento a pedido do titular e obrigacao legal, e a especificacao nao
   * trata do assunto em nenhum ponto: nem Console SPEC, nem Regras de Operacao,
   * mencionam titular, LGPD ou retencao.
   *
   * A alternativa seria uma rota solta fora da fronteira de comando, e ela seria
   * pior: a acao e IRREVERSIVEL e precisa de ator, motivo e auditoria, que e
   * exatamente o que a fronteira entrega. A divergencia esta registrada no
   * ADR-0019 para o dono confirmar.
   */
  "EraseDataSubject"
]);
var AdminCommandStatus = z10.enum([
  "requested",
  "validating",
  "accepted",
  "denied",
  "conflict",
  "executing",
  "reconciling",
  "applied",
  "partially_applied",
  "failed",
  "superseded"
]);
var AdminDenialCode = z10.enum([
  "AUTHENTICATION_REQUIRED",
  "STEP_UP_REQUIRED",
  "PERMISSION_DENIED",
  "OBJECT_NOT_FOUND_OR_NOT_VISIBLE",
  "INVALID_COMMAND",
  "INVALID_STATE_TRANSITION",
  "REVISION_CONFLICT",
  "POLICY_DENIED",
  "HARD_LIMIT_DENIED",
  "CAPABILITY_UNAVAILABLE",
  "RELEASE_DENIED",
  "DEPENDENCY_BLOCKED",
  "ALREADY_APPLIED",
  "RECONCILIATION_REQUIRED",
  "TEMPORARILY_UNAVAILABLE"
]);
var AdminCommandRequest = z10.object({
  command_type: AdminCommandType,
  target_ref: ObjectRef,
  expected_revision: z10.number().int().nonnegative().nullable().default(null),
  /** Motivo e obrigatorio em TODA mutacao (Back Office SPEC §15). */
  reason: z10.string().min(1),
  requested_effective_at: z10.string().datetime({ offset: true }).nullable().default(null),
  payload: z10.record(z10.unknown()).default({}),
  client_context: z10.object({
    surface: z10.literal("back_office"),
    view: z10.string().min(1)
  }).optional()
});
var AdminCommand = materialObject({
  command_id: z10.string().min(1),
  command_type: AdminCommandType,
  target_ref: ObjectRef,
  payload: z10.record(z10.unknown()).default({}),
  actor_id: z10.string().min(1),
  /** Escopos EFETIVOS resolvidos no servidor. O cliente nao os declara. */
  authority_scopes: z10.array(z10.string().min(1)),
  environment: DeployEnvironment,
  reason: z10.string().min(1),
  expected_revision: z10.number().int().nonnegative().nullable().default(null),
  idempotency_key: z10.string().min(1),
  policy_refs: z10.array(z10.string()).default([]),
  requested_effective_at: z10.string().datetime({ offset: true }).nullable().default(null),
  requested_at: z10.string().datetime({ offset: true }),
  status: AdminCommandStatus,
  accepted_at: z10.string().datetime({ offset: true }).nullable().default(null),
  completed_at: z10.string().datetime({ offset: true }).nullable().default(null),
  result_refs: z10.array(ObjectRef).default([]),
  denial_or_failure_code: AdminDenialCode.nullable().default(null)
}).superRefine((c, ctx) => {
  if (c.status === "accepted" && c.accepted_at === null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["accepted_at"],
      message: "status=accepted exige accepted_at dur\xE1vel"
    });
  }
  if ((c.status === "denied" || c.status === "conflict") && c.denial_or_failure_code === null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["denial_or_failure_code"],
      message: "denied/conflict exige codigo estavel de negacao"
    });
  }
});
var ConfigurationLevel = z10.enum(["operational", "policy", "hard"]);
var ConfigurationDefinition = z10.object({
  parameter_key: z10.string().min(1),
  display_name: z10.string().min(1),
  description: z10.string().min(1),
  class: ConfigurationClass,
  level: ConfigurationLevel,
  value_schema_ref: z10.string().min(1),
  unit: z10.string().nullable().default(null),
  allowed_range: z10.record(z10.unknown()).nullable().default(null),
  owner: z10.string().min(1),
  permitted_scopes: z10.array(z10.string().min(1)),
  /** Chave secreta expoe apenas `secret_ref`; o valor nunca e projetado. */
  secret: z10.boolean(),
  impact_policy_ref: z10.string().min(1)
}).superRefine((d, ctx) => {
  const ownerOnly = d.class === "constitutional" || d.class === "hard_limit";
  const adminScopes = d.permitted_scopes.filter(
    (s) => s.startsWith("configuration:change:") && !s.endsWith(":owner")
  );
  if (ownerOnly && adminScopes.some((s) => s !== `configuration:change:${d.class}`)) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["permitted_scopes"],
      message: `AT-131/AT-142: classe ${d.class} nao aceita escopo de alteracao de outra classe`
    });
  }
  if (d.class === "capability" && d.permitted_scopes.some((s) => s.startsWith("configuration:change:"))) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["permitted_scopes"],
      message: "CAPABILITY e derivada de validacao de integracao: read-only na UI"
    });
  }
});
var ConfigurationVersion = z10.object({
  configuration_version_id: z10.string().min(1),
  parameter_key: z10.string().min(1),
  value: z10.unknown().nullable().default(null),
  secret_ref: z10.string().min(1).nullable().default(null),
  version: z10.number().int().positive(),
  effective_from: z10.string().datetime({ offset: true }),
  effective_until: z10.string().datetime({ offset: true }).nullable().default(null),
  changed_by: z10.string().min(1),
  reason: z10.string().min(1),
  decision_ref: z10.string().min(1),
  rollback_value: z10.unknown().nullable().default(null),
  status: z10.enum(["proposed", "scheduled", "active", "rolled_back", "superseded", "rejected"])
});
var EscalationRecord = materialObject({
  escalation_id: z10.string().min(1),
  domain: z10.enum([
    "editorial",
    "fact",
    "brand",
    "legal",
    "safety",
    "voice",
    "cost",
    "release",
    "publishing",
    "community",
    "commercial",
    "operations"
  ]),
  severity: Severity,
  reason_code: z10.string().min(1),
  summary: z10.string().min(1),
  affected_refs: z10.array(ObjectRef).default([]),
  evidence_refs: z10.array(ObjectRef).default([]),
  required_scopes: z10.array(z10.string().min(1)).min(1),
  assigned_to: z10.string().min(1).nullable().default(null),
  due_at: z10.string().datetime({ offset: true }).nullable().default(null),
  status: z10.enum([
    "open",
    "assigned",
    "under_review",
    "decided",
    "applied",
    "expired",
    "superseded"
  ]),
  decision_ref: z10.string().min(1).nullable().default(null)
});
var ExceptionRecord = materialObject({
  exception_id: z10.string().min(1),
  policy_ref: z10.string().min(1),
  scope_refs: z10.array(ObjectRef).min(1),
  granted_by: z10.string().min(1),
  authority_ref: z10.string().min(1),
  reason: z10.string().min(1),
  compensating_controls: z10.array(z10.string()).default([]),
  valid_from: z10.string().datetime({ offset: true }),
  /** Excecao SEM expiry nao existe: por isso `valid_until` nao e nullable. */
  valid_until: z10.string().datetime({ offset: true }),
  revocation_conditions: z10.array(z10.string()).default([]),
  status: z10.enum(["proposed", "active", "expired", "revoked", "rejected"]),
  decision_ref: z10.string().min(1)
}).refine((e) => Date.parse(e.valid_until) > Date.parse(e.valid_from), {
  message: "valid_until deve ser posterior a valid_from",
  path: ["valid_until"]
});
var KPIDefinition = z10.object({
  kpi_id: z10.string().regex(/^KPI-\d{3}$/),
  name: z10.string().min(1),
  definition: z10.string().min(1),
  formula_ref: z10.string().min(1),
  unit: z10.string().min(1),
  objective_direction: z10.enum(["higher", "lower", "target_range", "informational"]),
  supported_dimensions: z10.array(z10.string()).default([]),
  required_sources: z10.array(z10.string().min(1)).min(1),
  minimum_sample_policy: z10.string().min(1).nullable().default(null),
  freshness_policy: z10.string().min(1),
  owner: z10.string().min(1),
  definition_version: z10.string().min(1),
  /** Composto exige policy de pesos APROVADA antes de exibir valor (D-053). */
  composite: z10.boolean().default(false),
  status: z10.enum(["proposed", "active", "superseded", "retired"])
});
var KPIStatus = z10.enum([
  "available",
  "estimated",
  "unavailable",
  "not_configured",
  "stale",
  "insufficient_sample",
  /**
   * Cobertura de CLASSIFICAÇÃO insuficiente (decisão do dono, 2026-09-10).
   *
   * Distinto de `insufficient_sample`, e a distinção tem consequência: o
   * primeiro fala de TAMANHO da amostra e se resolve esperando mais audiência;
   * este fala de COBERTURA e se resolve classificando o que já existe.
   * Colapsar os dois faria a tela mandar quem lê tentar a coisa errada.
   */
  "not_enough_classified_audience"
]);
var KPISnapshot = z10.object({
  kpi_snapshot_id: z10.string().min(1),
  kpi_id: z10.string().regex(/^KPI-\d{3}$/),
  definition_version: z10.string().min(1),
  value: z10.number().nullable().default(null),
  unit: z10.string().min(1),
  status: KPIStatus,
  numerator: z10.number().nullable().default(null),
  denominator: z10.number().nullable().default(null),
  dimensions: z10.record(z10.unknown()).default({}),
  window: TimeWindow,
  confidence: z10.number().min(0).max(1).nullable().default(null),
  evidence_level: EvidenceLevel.nullable().default(null),
  calculated_at: z10.string().datetime({ offset: true }),
  data_through: z10.string().datetime({ offset: true }).nullable().default(null),
  source_refs: z10.array(ObjectRef).default([]),
  policy_version: z10.string().min(1),
  notes: z10.array(z10.string()).default([])
}).superRefine((k, ctx) => {
  const noValue = [
    "unavailable",
    "not_configured",
    "insufficient_sample",
    // Sem este membro, o schema aceitaria nota agregada com cobertura
    // insuficiente, que e exatamente o que a decisao de 2026-09-10 proibe.
    "not_enough_classified_audience"
  ];
  if (noValue.includes(k.status) && k.value !== null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["value"],
      message: `AT-149/151/155: status=${k.status} exige value=null`
    });
  }
  if (k.denominator !== null && k.denominator === 0 && k.value !== null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["value"],
      message: "AT-155: denominador zero exige value=null e status insufficient_sample/unavailable"
    });
  }
  if (k.status === "estimated" && k.confidence === null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["confidence"],
      message: "AT-152: valor estimado exige confianca"
    });
  }
  if (k.status === "available" && k.data_through === null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["data_through"],
      message: "AT-150: valor available exige data_through para avaliar frescor"
    });
  }
});
var AttentionItem = z10.object({
  attention_id: z10.string().min(1),
  condition_ref: ObjectRef,
  severity: Severity,
  summary: z10.string().min(1),
  required_scopes: z10.array(z10.string().min(1)).min(1),
  owner_id: z10.string().min(1).nullable().default(null),
  due_at: z10.string().datetime({ offset: true }).nullable().default(null),
  status: z10.enum(["open", "acknowledged", "resolved", "expired", "superseded"]),
  created_at: z10.string().datetime({ offset: true }),
  acknowledged_at: z10.string().datetime({ offset: true }).nullable().default(null),
  resolved_at: z10.string().datetime({ offset: true }).nullable().default(null),
  resolution_ref: ObjectRef.nullable().default(null)
}).superRefine((a, ctx) => {
  if (a.status === "acknowledged" && a.resolved_at !== null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["resolved_at"],
      message: "AT-156: reconhecimento nao resolve a condicao de dominio"
    });
  }
  if (a.status === "resolved" && a.resolution_ref === null) {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      path: ["resolution_ref"],
      message: "resolved exige referencia ao objeto que resolveu a condicao"
    });
  }
});
var ProjectionWatermark = z10.object({
  projection_name: z10.string().min(1),
  projection_version: z10.string().min(1),
  last_processed_event_id: z10.string().min(1).nullable().default(null),
  last_processed_at: z10.string().datetime({ offset: true }).nullable().default(null),
  lag_seconds: z10.number().nonnegative().nullable().default(null),
  freshness_status: z10.enum(["fresh", "stale", "rebuilding", "unavailable"])
});

// src/authz.ts
import { z as z11 } from "zod";
var Role = z11.enum([
  "OWNER",
  "OPERATIONS_ADMIN",
  "DOMAIN_REVIEWER",
  "ANALYST",
  "AUDITOR",
  "SERVICE_IDENTITY"
]);
var GovernanceDomain = z11.enum(["fact", "brand", "legal", "safety", "voice", "editorial"]);
var SCOPES = [
  "dashboard:read",
  "content:read",
  "content:hold",
  "content:cancel",
  "agenda:read",
  "agenda:draft",
  "agenda:change",
  "audience:aggregate:read",
  "audience:restricted:read",
  /**
   * Segmento ganha escopo PROPRIO, de leitura e de escrita (resposta do
   * Ferrari, 14/09, item 1.3).
   *
   * Ate aqui, salvar segmento exigia a conjuncao `audience:aggregate:read` +
   * `configuration:change:operating_policy`. A conjuncao fechava para os papeis
   * certos, mas pelo motivo errado: emprestava o escopo de MUDAR POLITICA
   * OPERACIONAL a uma acao que nao muda politica nenhuma. A consequencia e que
   * um grant nao consegue separar as duas coisas: dar a alguem o direito de
   * salvar um segmento daria junto o de alterar politica operacional, e tirar
   * um exigiria tirar o outro. Com escopo dedicado, o grant fica do tamanho
   * exato da acao.
   *
   * DOIS escopos, e nao um, pela mesma razao dos direitos do titular abaixo:
   * quem so precisa LER a lista de segmentos nao precisa poder apagar segmento.
   * O item 1.3 nomeia deletar entre as acoes cobertas pelo write.
   */
  "audience:segment:read",
  "audience:segment:write",
  "community:aggregate:read",
  "conversation:restricted:read",
  "commercial:aggregate:read",
  "commercial:case:read",
  "governance:read",
  "escalation:decide:fact",
  "escalation:decide:brand",
  "escalation:decide:legal",
  "escalation:decide:safety",
  "escalation:decide:voice",
  "escalation:decide:editorial",
  "exception:grant:fact",
  "exception:grant:brand",
  "exception:grant:legal",
  "exception:grant:safety",
  "exception:grant:voice",
  "exception:grant:editorial",
  "exception:revoke:fact",
  "exception:revoke:brand",
  "exception:revoke:legal",
  "exception:revoke:safety",
  "exception:revoke:voice",
  "exception:revoke:editorial",
  "system:pause",
  "system:resume",
  "category:pause",
  "category:resume",
  "configuration:read",
  "configuration:change:constitutional",
  "configuration:change:hard_limit",
  "configuration:change:operating_policy",
  "configuration:change:learning_allowlisted",
  "configuration:change:experimental",
  "learning:approve",
  "learning:rollback",
  "audit:read",
  "audit:export",
  "incident:acknowledge",
  /**
   * Direitos do titular (LGPD). DOIS escopos, e nao um.
   *
   * Exportar e leitura de dado de TERCEIRO, e quem atende um pedido de acesso
   * nao precisa poder apagar. Apagar e irreversivel. Um escopo unico daria
   * poder de apagamento a quem so precisava responder um pedido de acesso.
   */
  "data_subject:export",
  "data_subject:erase"
];
var Scope = z11.enum(SCOPES);
var ROLE_MAX_SCOPES = {
  OWNER: SCOPES,
  OPERATIONS_ADMIN: [
    "dashboard:read",
    "content:read",
    "agenda:read",
    "agenda:draft",
    "audience:aggregate:read",
    // "Os roles atualmente autorizados permanecem os mesmos" (resposta do
    // Ferrari, 14/09, item 1.3). MEDIDO, e nao deduzido: os papeis que hoje
    // passam na conjuncao `audience:aggregate:read` +
    // `configuration:change:operating_policy` sao OWNER e OPERATIONS_ADMIN.
    // OWNER recebe o catalogo inteiro; entao o unico envelope que precisa citar
    // o write e este. Se algum dia o envelope perder
    // `configuration:change:operating_policy`, o direito de salvar segmento
    // fica onde esta, que e o ponto de ter escopo proprio.
    "audience:segment:read",
    "audience:segment:write",
    "community:aggregate:read",
    "commercial:aggregate:read",
    "governance:read",
    "configuration:read",
    "configuration:change:operating_policy",
    "configuration:change:experimental",
    "system:pause",
    "audit:read",
    "audit:export",
    "incident:acknowledge"
    // `policy` na matriz: category pause/resume, content hold/cancel e
    // learning approve/rollback exigem grant explicito, nunca vem do papel.
  ],
  DOMAIN_REVIEWER: [
    "dashboard:read",
    "content:read",
    "governance:read",
    "configuration:read"
    // escalation:decide:<dominio> e exception:*:<dominio> vem por grant do
    // dominio atribuido, jamais do papel inteiro.
  ],
  ANALYST: [
    "dashboard:read",
    "content:read",
    "agenda:read",
    "audience:aggregate:read",
    // Leitura de segmento acompanha `audience:aggregate:read`, que e quem hoje
    // abre a tela de segmentos (resposta do Ferrari, 14/09, item 1.3: os papeis
    // autorizados permanecem os mesmos). Ler nao e salvar: o write fica de
    // fora, e e por isso que sao dois escopos.
    "audience:segment:read",
    "community:aggregate:read",
    "commercial:aggregate:read",
    "governance:read",
    "configuration:read",
    "audit:read"
  ],
  AUDITOR: [
    "dashboard:read",
    "content:read",
    "agenda:read",
    "audience:aggregate:read",
    // Mesma regra do ANALYST: a leitura segue `audience:aggregate:read`, e o
    // write nao entra em papel que nao muta nada.
    "audience:segment:read",
    "community:aggregate:read",
    "commercial:aggregate:read",
    "governance:read",
    "configuration:read",
    "audit:read",
    "audit:export"
  ]
};
var SERVICE_IDENTITY_MAX_SCOPES = ["dashboard:read"];
var RESTRICTED_SCOPES = [
  "audience:restricted:read",
  "conversation:restricted:read",
  "commercial:case:read",
  // A exportacao devolve TODO o dado que o sistema guarda sobre uma pessoa, num
  // documento so. E o dado mais restrito que este produto produz, e por isso
  // exige grant explicito e emite auditoria de acesso sensivel.
  "data_subject:export"
];
var OWNER_ONLY_SCOPES = [
  "system:resume",
  "agenda:change",
  "configuration:change:constitutional",
  "configuration:change:hard_limit",
  // Apagamento e IRREVERSIVEL, e o dado nao volta de backup sem desfazer o
  // proprio apagamento. Fica com o dono, ao lado de teto duro.
  "data_subject:erase"
];
var Requirement = z11.object({
  /** Escopos que TODOS precisam estar presentes. */
  required_scopes: z11.array(Scope).min(1),
  /** Step-up (MFA recente) obrigatorio nestes ambientes. */
  step_up_environments: z11.array(DeployEnvironment).default([]),
  /** Motivo textual e obrigatorio em toda mutacao (Back Office SPEC §15). */
  reason_required: z11.literal(true),
  /** Preview de impacto obrigatorio antes da confirmacao. */
  impact_preview_required: z11.boolean(),
  /**
   * Pausa de emergencia NAO pode ser atrasada por step-up bloqueante
   * (Back Office SPEC §16: "recommended, not blocking emergency").
   */
  emergency: z11.boolean().default(false)
});
var ALL_ENVS = ["development", "sandbox", "staging", "production"];
var PROD_ONLY = ["production"];
var COMMAND_REQUIREMENTS = {
  PauseSystem: {
    required_scopes: ["system:pause"],
    step_up_environments: [],
    reason_required: true,
    impact_preview_required: true,
    emergency: true
  },
  ResumeSystem: {
    required_scopes: ["system:resume"],
    step_up_environments: ALL_ENVS,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  PauseCategory: {
    required_scopes: ["category:pause"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ResumeCategory: {
    required_scopes: ["category:resume"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  HoldTopic: {
    required_scopes: ["content:hold"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ReleaseTopicHold: {
    required_scopes: ["content:hold"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  HoldAgenda: {
    required_scopes: ["agenda:change"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ReleaseAgendaHold: {
    required_scopes: ["agenda:change"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  CancelContent: {
    required_scopes: ["content:cancel"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ChangeAgenda: {
    required_scopes: ["agenda:change"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ChangeHardLimit: {
    required_scopes: ["configuration:change:hard_limit"],
    step_up_environments: ALL_ENVS,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ChangeOperatingPolicy: {
    required_scopes: ["configuration:change:operating_policy"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  GrantException: {
    required_scopes: ["governance:read"],
    step_up_environments: ALL_ENVS,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  RevokeException: {
    required_scopes: ["governance:read"],
    step_up_environments: ALL_ENVS,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  DecideEscalation: {
    required_scopes: ["governance:read"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  ApproveParameterChange: {
    required_scopes: ["learning:approve"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  RejectParameterChange: {
    required_scopes: ["learning:approve"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  RollbackParameterChange: {
    required_scopes: ["learning:rollback"],
    step_up_environments: PROD_ONLY,
    reason_required: true,
    impact_preview_required: true,
    emergency: false
  },
  AcknowledgeIncident: {
    required_scopes: ["incident:acknowledge"],
    step_up_environments: [],
    reason_required: true,
    impact_preview_required: false,
    emergency: false
  },
  EraseDataSubject: {
    required_scopes: ["data_subject:erase"],
    // Step-up em TODO ambiente, como o teto duro. A acao e irreversivel, e o
    // dado nao volta de backup sem desfazer o proprio apagamento.
    step_up_environments: ALL_ENVS,
    reason_required: true,
    // Preview obrigatorio, e nao por formalidade: e ele que mostra QUANTAS
    // linhas saem e se existe pendencia externa. Apagar as tres linhas erradas
    // por causa de um handle digitado errado nao tem desfazer.
    impact_preview_required: true,
    // NAO e emergencia. Emergencia dispensa step-up bloqueante, e um pedido de
    // titular tem prazo legal em dias, nunca em minutos: dispensar o step-up
    // aqui seria transformar um prazo confortavel em risco de apagamento
    // indevido.
    emergency: false
  }
};
function domainScopeFor(command, domain) {
  if (command === "DecideEscalation") return `escalation:decide:${domain}`;
  if (command === "GrantException") return `exception:grant:${domain}`;
  return `exception:revoke:${domain}`;
}
function configurationScopeFor(cls) {
  if (cls === "capability") return null;
  return `configuration:change:${cls}`;
}

// src/events.ts
import { z as z12 } from "zod";
var DOMAIN_EVENT_TYPES = [
  "CONTENT_OPPORTUNITY_CREATED",
  "BREAKING_OPPORTUNITY_CREATED",
  "CONTENT_SELECTED",
  "CONTENT_DEFERRED",
  "SCRIPT_VERSION_CREATED",
  "GOVERNANCE_REVISE",
  "GOVERNANCE_APPROVED",
  "CONTENT_BLOCKED",
  "COST_GATE_DENIED",
  "RELEASE_DENIED",
  "PRODUCTION_REQUESTED",
  "HEYGEN_RENDERED",
  "TECHNICAL_QA_PASSED",
  "PUBLICATION_QUEUED",
  "POST_PUBLISHED",
  "PUBLISH_FAILED",
  "METRICS_CAPTURED",
  "BREAKOUT_DETECTED",
  "LEARNING_SIGNAL_CREATED",
  "RECOMMENDATION_CREATED",
  "PARAMETER_CHANGED",
  "NEW_INTERACTION",
  "COMMERCIAL_INTENT_DETECTED",
  "LEAD_QUALIFIED",
  "FOLLOWUP_SENT",
  "MEETING_BOOKED",
  "SYSTEM_PAUSED",
  // Eventos de rejeicao deterministica exigidos pelo orquestrador (Workflow §2/§17).
  "INVALID_TRANSITION_REJECTED",
  "DUPLICATE_COMMAND_IGNORED",
  "POISON_EVENT_DEAD_LETTERED",
  // Ciclo de render assincrono (Workflow §7). A spec nomeia o sucesso
  // ('HEYGEN_RENDERED') e o pedido ('PRODUCTION_REQUESTED'), mas nao os dois
  // desfechos que custam dinheiro sem entregar video: falha confirmada e
  // despacho com estado remoto DESCONHECIDO. Sem eventos proprios, o
  // segundo ficaria indistinguivel do primeiro no log — e sao opostos: um
  // devolve a reserva, o outro nao pode devolver.
  "RENDER_FAILED",
  "RENDER_AMBIGUOUS",
  "RENDER_RECONCILED"
];
var CONTROL_EVENT_TYPES = [
  "ADMIN_COMMAND_REQUESTED",
  "ADMIN_COMMAND_ACCEPTED",
  "ADMIN_COMMAND_DENIED",
  "ADMIN_COMMAND_CONFLICTED",
  "ADMIN_COMMAND_APPLIED",
  "ADMIN_COMMAND_PARTIALLY_APPLIED",
  "ADMIN_COMMAND_FAILED",
  "SYSTEM_RESUME_REQUESTED",
  "SYSTEM_RESUME_DENIED",
  "SYSTEM_RESUMED",
  "CATEGORY_PAUSED",
  "CATEGORY_RESUMED",
  "TOPIC_HELD",
  "TOPIC_HOLD_RELEASED",
  "AGENDA_HELD",
  "AGENDA_HOLD_RELEASED",
  "CONTENT_CANCELLED_BY_OWNER",
  "AGENDA_CHANGE_REQUESTED",
  "AGENDA_CHANGE_APPLIED",
  "HARD_LIMIT_CHANGE_REQUESTED",
  "HARD_LIMIT_CHANGED",
  "OPERATING_POLICY_CHANGED",
  "EXCEPTION_GRANTED",
  "EXCEPTION_REVOKED",
  "EXCEPTION_EXPIRED",
  "ESCALATION_DECIDED",
  "PARAMETER_CHANGE_APPROVED",
  "PARAMETER_CHANGE_REJECTED",
  "PARAMETER_ROLLBACK_REQUESTED",
  "PARAMETER_ROLLED_BACK",
  /**
   * A versao foi CRIADA com vigencia futura, e nao entrou em vigor agora.
   *
   * Evento proprio, e nao o mesmo da mudanca aplicada: quem le a auditoria tem
   * de distinguir "o valor mudou" de "o valor vai mudar em tal data". Colapsar
   * os dois faria uma mudanca agendada parecer que ja esta valendo.
   */
  "PARAMETER_CHANGE_SCHEDULED",
  /** A versao agendada ENTROU em vigor, e a anterior foi supersedida. */
  "PARAMETER_CHANGE_ACTIVATED",
  "INCIDENT_ACKNOWLEDGED",
  "SENSITIVE_RECORD_ACCESSED",
  "AUDIT_EXPORT_CREATED",
  /**
   * Direitos do titular (LGPD).
   *
   * NENHUM dos dois carrega o handle no payload: o log e append-only, e um
   * handle aqui sobreviveria ao apagamento, desfazendo-o. O que viaja e a
   * impressao de uma via, que permite reencontrar o recibo a partir do handle e
   * nao permite o caminho inverso.
   */
  "DATA_SUBJECT_EXPORTED",
  "DATA_SUBJECT_ERASED",
  /**
   * Retencao aplicada: o que perdeu a funcao foi apagado.
   *
   * O payload carrega as JANELAS, e nao so as contagens: sem elas, uma contagem
   * alta amanha seria indistinguivel de alguem ter encurtado a janela.
   */
  "RETENTION_APPLIED"
];
var EVENT_TYPES = [...DOMAIN_EVENT_TYPES, ...CONTROL_EVENT_TYPES];
var EventType = z12.enum(EVENT_TYPES);
var DomainEventType = z12.enum(DOMAIN_EVENT_TYPES);
var ControlEventType = z12.enum(CONTROL_EVENT_TYPES);
var SIDE_EFFECTING_EVENT_TYPES = [
  "HEYGEN_RENDERED",
  // Despacho com estado remoto DESCONHECIDO: pode ter havido render pago do
  // outro lado, e um replay que o reemitisse afirmaria um gasto que talvez nao
  // exista.
  "RENDER_AMBIGUOUS",
  "POST_PUBLISHED",
  "PUBLISH_FAILED",
  "FOLLOWUP_SENT",
  "MEETING_BOOKED"
];
var RESTRICTED_ACCESS_EVENT_TYPE = "SENSITIVE_RECORD_ACCESSED";

// src/workflow.ts
var CONTENT_TRANSITIONS = {
  DISCOVERED: ["UNDER_EDITORIAL_REVIEW", "REJECTED", "DEFERRED", "EVERGREEN"],
  UNDER_EDITORIAL_REVIEW: ["SELECTED", "DEFERRED", "REJECTED", "EVERGREEN"],
  SELECTED: ["CREATIVE_BRIEF", "CANCELLED", "HELD"],
  DEFERRED: ["UNDER_EDITORIAL_REVIEW", "EVERGREEN", "RETIRED"],
  REJECTED: ["RETIRED"],
  EVERGREEN: ["UNDER_EDITORIAL_REVIEW", "REPURPOSE_REQUESTED", "RETIRED"],
  CREATIVE_BRIEF: ["SCRIPTING", "CANCELLED", "HELD"],
  SCRIPTING: ["PREPRODUCTION_GOVERNANCE", "CANCELLED", "HELD"],
  // D-005: governanca semantica ANTES de qualquer producao custosa.
  PREPRODUCTION_GOVERNANCE: [
    "REVISION",
    "BLOCKED",
    "ESCALATED",
    "SEMANTICALLY_APPROVED",
    "CANCELLED",
    "HELD"
  ],
  // Loop de revisao: volta ao script, nunca "tenta de novo" as cegas (Workflow §5).
  REVISION: ["SCRIPTING", "ESCALATED", "REJECTED", "CANCELLED"],
  BLOCKED: ["REVISION", "ESCALATED", "REJECTED", "RETIRED"],
  ESCALATED: ["REVISION", "SEMANTICALLY_APPROVED", "REJECTED", "BLOCKED", "CANCELLED"],
  SEMANTICALLY_APPROVED: ["WAITING_PRODUCTION", "HELD", "CANCELLED"],
  // COST_CHECK so existe no caminho custoso (Digital Twin). Negacao => HELD, sem retry.
  WAITING_PRODUCTION: ["COST_CHECK", "PRODUCING", "HELD", "CANCELLED"],
  COST_CHECK: ["PRODUCING", "HELD", "CANCELLED"],
  PRODUCING: ["TECHNICAL_QA", "PUBLISH_FAILED", "HELD", "CANCELLED"],
  // QA tecnico reprova => volta a producao. Mudanca SEMANTICA volta a governanca.
  TECHNICAL_QA: ["READY_TO_PUBLISH", "PRODUCING", "PREPRODUCTION_GOVERNANCE", "HELD", "CANCELLED"],
  READY_TO_PUBLISH: ["RELEASE_CHECK", "HELD", "CANCELLED"],
  RELEASE_CHECK: ["QUEUED", "HELD", "CANCELLED"],
  QUEUED: ["SCHEDULED", "PUBLISHING", "HELD", "CANCELLED"],
  SCHEDULED: ["PUBLISHING", "QUEUED", "HELD", "CANCELLED"],
  PUBLISHING: ["PUBLISHED", "PUBLISH_FAILED", "HELD"],
  PUBLISHED: ["MEASURING"],
  PUBLISH_FAILED: ["QUEUED", "HELD", "CANCELLED"],
  // Pausa/hold preserva estado: volta para a fila, nao para o inicio (Workflow §21).
  HELD: [
    "SELECTED",
    "SCRIPTING",
    "PREPRODUCTION_GOVERNANCE",
    "WAITING_PRODUCTION",
    "READY_TO_PUBLISH",
    "QUEUED",
    "CANCELLED",
    "RETIRED"
  ],
  CANCELLED: ["RETIRED"],
  MEASURING: ["LEARNED"],
  LEARNED: ["EVERGREEN", "REPURPOSE_REQUESTED", "RETIRED"],
  REPURPOSE_REQUESTED: ["UNDER_EDITORIAL_REVIEW", "RETIRED"],
  RETIRED: []
};
var CONTENT_TERMINAL_STATES = ["RETIRED"];
var CONTENT_STATES_WITH_EXTERNAL_EFFECT = [
  "PRODUCING",
  "PUBLISHING",
  "PUBLISHED"
];
function isValidContentTransition(from, to) {
  return CONTENT_TRANSITIONS[from].includes(to);
}
var ADMIN_COMMAND_TRANSITIONS = {
  requested: ["validating"],
  validating: ["denied", "conflict", "accepted"],
  // `accepted` NAO e `applied`: existe um estado de execucao entre os dois (D-047).
  accepted: ["executing", "superseded"],
  executing: ["applied", "partially_applied", "reconciling", "failed"],
  reconciling: ["applied", "partially_applied", "failed"],
  denied: [],
  conflict: [],
  applied: [],
  partially_applied: [],
  failed: [],
  superseded: []
};
var ADMIN_COMMAND_TERMINAL_STATES = [
  "denied",
  "conflict",
  "applied",
  "partially_applied",
  "failed",
  "superseded"
];
function isValidAdminCommandTransition(from, to) {
  return ADMIN_COMMAND_TRANSITIONS[from].includes(to);
}
var ESCALATION_STATES = [
  "open",
  "assigned",
  "under_review",
  "decided",
  "applied",
  "expired",
  "superseded"
];
var ESCALATION_TRANSITIONS = {
  open: ["assigned", "expired", "superseded"],
  assigned: ["under_review", "open", "expired", "superseded"],
  under_review: ["decided", "expired", "superseded"],
  decided: ["applied", "superseded"],
  applied: [],
  expired: [],
  superseded: []
};
var CONFIGURATION_CHANGE_STATES = [
  "draft",
  "validated",
  "owner_approval_required",
  "approved",
  "scheduled",
  "applied",
  "monitoring",
  "confirmed",
  "rolled_back",
  "superseded",
  "rejected"
];
var CONFIGURATION_CHANGE_TRANSITIONS = {
  draft: ["validated", "rejected"],
  validated: ["owner_approval_required", "approved", "rejected"],
  owner_approval_required: ["approved", "rejected"],
  approved: ["scheduled", "applied", "superseded"],
  scheduled: ["applied", "superseded", "rejected"],
  applied: ["monitoring", "rolled_back"],
  monitoring: ["confirmed", "rolled_back"],
  confirmed: ["superseded"],
  rolled_back: [],
  superseded: [],
  rejected: []
};
var LOYALTY_STATES = [
  "NEW_SOURCE",
  "TRANSCRIBING",
  "MAPPING",
  "OPPORTUNITIES_READY",
  "RECORDED_EMBARGO",
  "SCHEDULED",
  "PUBLIC",
  "REUSABLE",
  "HOLD",
  "REVOKED"
];
var LOYALTY_TRANSITIONS = {
  NEW_SOURCE: ["TRANSCRIBING", "HOLD", "REVOKED"],
  TRANSCRIBING: ["MAPPING", "HOLD", "REVOKED"],
  MAPPING: ["OPPORTUNITIES_READY", "HOLD", "REVOKED"],
  // Processar sob embargo e PERMITIDO; publicar nao (D-011, AT-030).
  OPPORTUNITIES_READY: ["RECORDED_EMBARGO", "SCHEDULED", "HOLD", "REVOKED"],
  RECORDED_EMBARGO: ["SCHEDULED", "PUBLIC", "HOLD", "REVOKED"],
  SCHEDULED: ["PUBLIC", "RECORDED_EMBARGO", "HOLD", "REVOKED"],
  PUBLIC: ["REUSABLE", "HOLD", "REVOKED"],
  REUSABLE: ["HOLD", "REVOKED"],
  HOLD: ["RECORDED_EMBARGO", "SCHEDULED", "PUBLIC", "REUSABLE", "REVOKED"],
  REVOKED: []
};
var LOYALTY_PUBLISHABLE_STATES = ["PUBLIC", "REUSABLE"];
var COMMUNITY_RESPONSE_STATES = [
  "NEW_INTERACTION",
  "CLASSIFIED",
  "MODERATION_POLICY",
  "RESPONSE_RISK",
  "SUGGESTED_RESPONSE",
  "REQUIRED_GOVERNANCE",
  "SENT",
  "HIDDEN",
  "HELD",
  "ESCALATED",
  "NO_ACTION"
];
var COMMUNITY_RESPONSE_TRANSITIONS = {
  NEW_INTERACTION: ["CLASSIFIED"],
  CLASSIFIED: ["MODERATION_POLICY", "NO_ACTION"],
  MODERATION_POLICY: ["RESPONSE_RISK", "HIDDEN", "NO_ACTION", "ESCALATED"],
  RESPONSE_RISK: ["SUGGESTED_RESPONSE", "ESCALATED", "NO_ACTION"],
  SUGGESTED_RESPONSE: ["REQUIRED_GOVERNANCE", "HELD", "ESCALATED"],
  REQUIRED_GOVERNANCE: ["SENT", "HELD", "ESCALATED", "NO_ACTION"],
  SENT: [],
  HIDDEN: ["NO_ACTION"],
  HELD: ["REQUIRED_GOVERNANCE", "ESCALATED", "NO_ACTION"],
  ESCALATED: ["REQUIRED_GOVERNANCE", "SENT", "HIDDEN", "NO_ACTION"],
  NO_ACTION: []
};
var COMMERCIAL_STATES = [
  "COMMERCIAL_INTENT",
  "SALES_OWNERSHIP",
  "DISCOVERY",
  "QUALIFYING",
  "QUALIFIED",
  "NOT_QUALIFIED",
  "EXISTING_CUSTOMER",
  "NURTURE",
  "MEETING_OFFERED",
  "MEETING_BOOKED",
  "FOLLOW_UP",
  "CLOSED"
];
var COMMERCIAL_TRANSITIONS = {
  COMMERCIAL_INTENT: ["SALES_OWNERSHIP"],
  SALES_OWNERSHIP: ["DISCOVERY", "EXISTING_CUSTOMER"],
  DISCOVERY: ["QUALIFYING", "NOT_QUALIFIED", "EXISTING_CUSTOMER", "NURTURE", "CLOSED"],
  QUALIFYING: ["QUALIFIED", "NOT_QUALIFIED", "EXISTING_CUSTOMER", "NURTURE", "CLOSED"],
  QUALIFIED: ["MEETING_OFFERED", "NURTURE", "CLOSED"],
  MEETING_OFFERED: ["MEETING_BOOKED", "FOLLOW_UP", "NURTURE", "CLOSED"],
  FOLLOW_UP: ["MEETING_BOOKED", "NURTURE", "CLOSED"],
  MEETING_BOOKED: ["CLOSED"],
  // Cliente existente vai para SUPORTE, nunca vira Lead (AT-122).
  EXISTING_CUSTOMER: ["CLOSED"],
  NOT_QUALIFIED: ["NURTURE", "CLOSED"],
  NURTURE: ["DISCOVERY", "CLOSED"],
  CLOSED: []
};
var PARAMETER_CHANGE_STATES = [
  "proposed",
  "validated",
  "applied",
  "monitored",
  "retained",
  "rolled_back",
  "rejected"
];
var PARAMETER_CHANGE_TRANSITIONS = {
  proposed: ["validated", "rejected"],
  validated: ["applied", "rejected"],
  applied: ["monitored", "rolled_back"],
  monitored: ["retained", "rolled_back"],
  retained: [],
  rolled_back: [],
  rejected: []
};
var METRIC_SNAPSHOT_SCHEDULE_HOURS = [1, 6, 24, 72, 168];

// src/registry.ts
var SCHEMA_REGISTRY = {
  // §2 editorial
  ContentOpportunity,
  EditorialDecision,
  ContentItem,
  CreativeBrief,
  ScriptVersion,
  SourceAsset,
  LoyaltyEpisode,
  CutOpportunity,
  // §3 governanca
  Claim,
  GovernanceReview,
  GovernanceBundle,
  GateResult,
  // §4 producao e publicacao
  ProductionRequest,
  ProductionAsset,
  PublicationPackage,
  PublicationRecord,
  CostEnvelope,
  CostLedgerEntry,
  // §5 metricas, learning e experimentos
  MetricSnapshot,
  Experiment,
  LearningSignal,
  Recommendation,
  ParameterChange,
  AudienceCohort,
  AudienceSignal,
  RelationshipEdge,
  // §6 estrategia
  StrategicAgenda,
  // §7 community e comercial
  Interaction,
  Conversation,
  Lead,
  FollowUpAction,
  CommercialEvidence,
  // §8 conhecimento, decisao e auditoria
  KnowledgeEntry,
  DecisionRecord,
  DomainEvent,
  // §11 plano de controle
  AdminCommand,
  ConfigurationDefinition,
  ConfigurationVersion,
  EscalationRecord,
  ExceptionRecord,
  KPIDefinition,
  KPISnapshot,
  AttentionItem,
  ProjectionWatermark
};
var NON_MATERIAL_SCHEMAS = [
  "DecisionRecord",
  "DomainEvent",
  "ConfigurationDefinition",
  "ConfigurationVersion",
  "KPIDefinition",
  "KPISnapshot",
  "AttentionItem",
  "ProjectionWatermark"
];
var SCHEMA_VERSION = "1.2.0";

// src/controle-do-sistema.ts
var SYSTEM_CONTROL_STATES = [
  "ON",
  "PAUSING",
  "PAUSED",
  "RESUME_CHECK",
  "RESUME_HOLD",
  "RESUMING"
];
var SYSTEM_CONTROL_TRANSITIONS = {
  ON: ["PAUSING", "PAUSED"],
  PAUSING: ["PAUSED"],
  PAUSED: ["RESUME_CHECK"],
  RESUME_CHECK: ["RESUME_HOLD", "RESUMING", "PAUSED"],
  RESUME_HOLD: ["RESUME_CHECK", "PAUSED"],
  RESUMING: ["ON", "RESUME_HOLD", "PAUSED"]
};
var ESTADOS_QUE_BLOQUEIAM_ACAO_EXTERNA = [
  "PAUSING",
  "PAUSED",
  "RESUME_CHECK",
  "RESUME_HOLD"
];
function permiteAcaoExterna(estado) {
  return !ESTADOS_QUE_BLOQUEIAM_ACAO_EXTERNA.includes(estado);
}
var RESUME_CHECK_RESULTS = ["PASS", "FAIL", "SKIP"];
var VERIFICACOES_DE_PRONTIDAO = [
  {
    id: "meta_auth",
    rotulo: "Autenticacao da Meta e do Instagram",
    bloqueiaRetomada: true,
    porque: "sem token valido, toda publicacao retomada falha na primeira chamada. Voltar para ON aqui seria voltar para uma fila que so produz erro."
  },
  {
    id: "publishing_capability",
    rotulo: "Capacidade de publicacao",
    bloqueiaRetomada: true,
    porque: "a capacidade e o que diz se o formato ainda e publicavel pela API. Retomar sem ela e descobrir o problema no efeito externo."
  },
  {
    id: "heygen",
    rotulo: "Capacidade e cota do HeyGen",
    bloqueiaRetomada: false,
    porque: "render e um caminho entre varios. Sem HeyGen, conteudo sem avatar continua podendo sair; bloquear tudo por isso seria parar o sistema inteiro por uma funcionalidade."
  },
  {
    id: "youtube_release",
    rotulo: "YouTube e Release Gate",
    bloqueiaRetomada: false,
    porque: "mesmo raciocinio do render: o YouTube e um canal entre varios, e o Release Gate dele so vale para o que sai por ali. Segurar a retomada inteira por um canal seria parar o Instagram por causa do YouTube."
  },
  {
    id: "drive",
    rotulo: "Google Drive (repositorio de ativos)",
    bloqueiaRetomada: false,
    porque: "e o repositorio de ativos. Sem ele a peca que depende de um ativo falha, e as que nao dependem seguem: bloquear a retomada inteira aqui pararia o que nao tem nada a ver com o Drive."
  },
  {
    id: "calendar",
    rotulo: "Google Calendar (quando o agendamento comercial esta ligado)",
    bloqueiaRetomada: true,
    porque: "quando o agendamento esta LIGADO, retomar sem calendario e prometer horario que nao existe para uma pessoa de verdade. Com o agendamento desligado esta verificacao e SKIP, e nao PASS."
  },
  {
    id: "email_alerts",
    rotulo: "Entrega de alerta por e-mail",
    bloqueiaRetomada: true,
    porque: "e o canal pelo qual o dono descobre que algo deu errado depois da retomada. Retomar sem ele e retomar as cegas."
  },
  {
    id: "budgets",
    rotulo: "Orcamentos",
    bloqueiaRetomada: true,
    porque: "orcamento estourado com o sistema voltando e a combinacao que gera fatura sem ninguem olhando. O teto e conferido ANTES de a fila andar."
  },
  {
    id: "quotas",
    rotulo: "Cotas de API e de render",
    bloqueiaRetomada: true,
    porque: "mesma familia do orcamento, do lado do fornecedor: cota esgotada transforma a retomada numa sequencia de recusas, e o custo disso e descobrir pelo cliente em vez de pela tela."
  },
  {
    id: "data_freshness",
    rotulo: "Frescor dos dados criticos",
    bloqueiaRetomada: false,
    porque: "dado velho degrada a decisao, e nao a seguranca. Aparece como aviso, porque bloquear por frescor deixaria o sistema irretomavel depois de uma pausa longa, que e exatamente quando ele mais precisa voltar."
  },
  {
    id: "backlog_freshness",
    rotulo: "Frescor do conteudo pendente",
    bloqueiaRetomada: false,
    porque: "noticia velha nao impede a retomada: ela e tratada item a item na revalidacao do backlog, que sabe expirar ou revalidar cada uma."
  },
  {
    id: "missed_windows",
    rotulo: "Janelas de publicacao perdidas",
    bloqueiaRetomada: false,
    porque: "janela perdida e o caso NORMAL depois de uma pausa. Ela vira reagendamento na revalidacao, e nao bloqueio."
  },
  {
    id: "duplicate_risk",
    rotulo: "Risco de publicacao duplicada",
    bloqueiaRetomada: true,
    porque: "publicacao com estado externo desconhecido pode ja ter saido. Retomar sem reconciliar e arriscar publicar de novo na conta do cliente, que e efeito externo irreversivel."
  },
  {
    id: "active_campaigns",
    rotulo: "Campanhas pagas ativas",
    bloqueiaRetomada: true,
    porque: "campanha ativa gasta dinheiro por conta propria. A V2 \xA7174 e explicita: campanha paga permanece pausada ate a reconferencia de elegibilidade e de orcamento."
  },
  {
    id: "audience_sync",
    rotulo: "Estado da sincronizacao de audiencia",
    bloqueiaRetomada: false,
    porque: "sincronizacao de audiencia alimenta a midia paga, que ja fica parada por conta propria. Bloquear duas vezes pela mesma causa produz dois bloqueios para um problema."
  },
  {
    id: "policies",
    rotulo: "Policies aprovadas e vigentes",
    bloqueiaRetomada: true,
    porque: "policy e o que decide o que pode ser dito. Retomar com policy ausente ou nao aprovada e voltar a produzir sem as regras que o dono aprovou."
  },
  {
    id: "hard_gates",
    rotulo: "Gates duros vigentes",
    bloqueiaRetomada: true,
    porque: "mesmo motivo da policy, do lado do codigo: sao os limites que nao dependem de julgamento, e um gate ausente nao aparece como negacao, aparece como permissao."
  },
  {
    id: "open_escalations",
    rotulo: "Escalacoes criticas em aberto",
    bloqueiaRetomada: true,
    porque: "e a unica das dezoito que ja existia antes da V2, como bloqueio da retomada. Continua: escalacao critica aberta significa que alguem parou o sistema por um motivo que ninguem resolveu."
  }
];
var IDS_DE_VERIFICACAO = VERIFICACOES_DE_PRONTIDAO.map((v) => v.id);
function vereditoDoSafeResume(resultados) {
  const desconhecidas = Object.keys(resultados).filter((id) => !IDS_DE_VERIFICACAO.includes(id));
  if (desconhecidas.length > 0) {
    throw new Error(
      `verificacao de prontidao fora do catalogo: ${desconhecidas.join(", ")}. O catalogo do SAFE_RESUME e fechado (Console SPEC v1.5 \xA7173).`
    );
  }
  const bloqueios = [];
  for (const v of VERIFICACOES_DE_PRONTIDAO) {
    if (!v.bloqueiaRetomada) continue;
    const r = resultados[v.id];
    if (r === void 0) {
      bloqueios.push(`${v.id}: nao foi executada, e verificacao que bloqueia conta como reprovada`);
      continue;
    }
    if (r === "FAIL") bloqueios.push(`${v.id}: ${v.rotulo}`);
  }
  return { veredito: bloqueios.length === 0 ? "PASS" : "HOLD", bloqueios };
}
var BACKLOG_DECISIONS = [
  "CONTINUE",
  "RESCHEDULE",
  "REVALIDATE",
  "EXPIRE",
  "CANCEL",
  "HUMAN_REVIEW"
];
var DECISOES_QUE_SEGURAM = [
  "REVALIDATE",
  "EXPIRE",
  "CANCEL",
  "HUMAN_REVIEW"
];
function liberaExecucao(decisao) {
  return !DECISOES_QUE_SEGURAM.includes(decisao);
}

// src/audiencia.ts
var AUDIENCE_SOURCE_TYPES = ["API", "INTERNAL_DATABASE", "FILE_UPLOAD"];
var AUDIENCE_INTEGRATION_MODES = ["DIRECT_API", "DATABASE_ADAPTER", "FILE"];
var AUDIENCE_SOURCE_STATUSES = [
  "CONNECTED",
  "DISCONNECTED",
  "DEGRADED",
  "AUTH_REQUIRED",
  "ERROR"
];
var AUDIENCE_APPROVAL_STATUSES = [
  "APPROVED",
  "PAUSED",
  "BLOCKED",
  "REQUIRES_REVIEW"
];
var AUDIENCE_APPROVAL_SCOPES = ["ENTIRE_SOURCE", "PARTIAL"];
var AUDIENCE_SOURCE_CAPABILITIES = [
  "query",
  "incremental_sync",
  "list_contacts",
  "list_companies",
  "write_back"
];
var AUDIENCE_FIELD_TYPES = ["STRING", "NUMBER", "BOOLEAN", "DATE", "ENUM"];
var HARD_EXCLUSION_REASONS = [
  "explicit_opt_out",
  "suppressed_or_deleted_record",
  "policy_blocked_record",
  "invalid_matching_identifier"
];
var UNKNOWN_EXCLUSION_MARKS = {
  explicit_opt_out: "UNKNOWN",
  suppressed_or_deleted_record: "UNKNOWN",
  policy_blocked_record: "UNKNOWN",
  invalid_matching_identifier: "UNKNOWN"
};
var SEEDED_HARD_EXCLUSION_POLICY = {
  explicit_opt_out: true,
  suppressed_or_deleted_record: true,
  policy_blocked_record: true,
  invalid_matching_identifier: true
};
var AUDIENCE_RECORD_KINDS = ["CONTACT", "COMPANY"];
var FILTER_OPERATORS = [
  "EQ",
  "NEQ",
  "CONTAINS",
  "IN",
  "NOT_IN",
  "GT",
  "GTE",
  "LT",
  "LTE",
  "BETWEEN",
  "IS_EMPTY",
  "IS_NOT_EMPTY"
];
var FILTER_OPERATORS_WITHOUT_VALUE = [
  "IS_EMPTY",
  "IS_NOT_EMPTY"
];
var FILTER_OPERATORS_WITH_LIST_VALUE = ["IN", "NOT_IN"];
var FILTER_OPERATORS_ORDERED = [
  "GT",
  "GTE",
  "LT",
  "LTE",
  "BETWEEN"
];
var FILTER_COMPOSITIONS = ["AND", "OR"];
function isFilterTree(no) {
  return Array.isArray(no.children);
}
var AUDIENCE_SEGMENT_MODES = ["DYNAMIC", "SNAPSHOT"];
var AUDIENCE_SEGMENT_STATUSES = ["ACTIVE", "PAUSED", "ERROR"];
var AUDIENCE_IMPORT_FILE_TYPES = ["CSV", "XLSX"];
var AUDIENCE_IMPORT_STATUSES = [
  "UPLOADED",
  "MAPPED",
  "VALIDATED",
  "IMPORTED",
  "FAILED"
];
var AUDIENCE_IMPORT_MAPPABLE_FIELDS = [
  "email",
  "phone",
  "first_name",
  "last_name",
  "company",
  "role",
  "city",
  "state",
  "zip",
  "country",
  "internal_id"
];
var AUDIENCE_MATCHING_FIELDS = ["email", "phone"];
var META_AUDIENCE_SYNC_MODES = ["MANUAL", "SCHEDULED"];
var META_AUDIENCE_BINDING_STATUSES = [
  "NOT_CREATED",
  "SYNCING",
  "READY",
  "PAUSED",
  "ERROR",
  "DELETED_REMOTE"
];
var AUDIENCE_SYNC_RUN_STATUSES = ["RUNNING", "SUCCESS", "PARTIAL", "FAILED"];
var SYNC_EXCLUSION_REASONS = [
  ...HARD_EXCLUSION_REASONS,
  "unnormalizable_identifier",
  "duplicate_identifier"
];

// src/midia-paga.ts
var PAID_AUTONOMY_MODES = [
  "MANUAL",
  "RECOMMEND_AND_APPROVE",
  "AUTO_WITHIN_LIMITS"
];
var PAID_MEDIA_PLAN_STATUSES = [
  "DRAFT",
  "RECOMMENDED",
  "REVIEW_REQUIRED",
  "APPROVED",
  "REJECTED"
];
var PAID_BUDGET_KINDS = ["DAILY", "LIFETIME"];
var PAID_CAMPAIGN_STATES = [
  "draft",
  "recommended",
  "review_required",
  "approved",
  "creating",
  "paused",
  "running",
  "hold",
  "completed",
  "failed",
  "archived"
];
var ESTADO_DA_CAMPANHA_NO_DOCUMENTO = {
  draft: "DRAFT",
  recommended: "RECOMMENDED",
  review_required: "REVIEW_REQUIRED",
  approved: "APPROVED",
  creating: "CREATING",
  paused: "PAUSED",
  // O unico par em que a grafia diverge, e a razao esta na docstring acima.
  running: "ACTIVE",
  hold: "HOLD",
  completed: "COMPLETED",
  failed: "FAILED",
  archived: "ARCHIVED"
};
var PAID_RIGHTS_PERMISSIONS = ["GRANTED", "DENIED", "UNKNOWN"];
var PAID_GOVERNANCE_RECHECK_TRIGGERS = [
  "NEW_AD_COPY",
  "NEW_HEADLINE",
  "NEW_CTA",
  "NEW_COMMERCIAL_CLAIM",
  "NEW_GUARANTEE",
  "NEW_OFFER_OR_PRICE",
  "NEW_SENSITIVE_TARGETING",
  "MATERIAL_POLICY_CHANGE"
];
var PAID_INSIGHTS_LEVELS = ["CAMPAIGN", "ADSET", "AD"];
var CPQF_PRECISIONS = ["EXACT", "ESTIMATED", "NOT_AVAILABLE"];
var PAID_OPTIMIZATION_RECOMMENDATIONS = [
  "KEEP",
  "PAUSE",
  "REALLOCATE",
  "SCALE_WITHIN_ENVELOPE",
  "CHANGE_AUDIENCE",
  "CHANGE_CREATIVE",
  "END"
];
var PAID_AUDIT_EVENTS = [
  "PAID_PLAN_CREATED",
  "PAID_PLAN_CHANGED",
  "PAID_PLAN_APPROVED",
  "PAID_PLAN_REJECTED",
  "PAID_CAMPAIGN_CREATE_REQUESTED",
  "PAID_CAMPAIGN_CREATED",
  "PAID_CAMPAIGN_ACTIVATED",
  "PAID_CAMPAIGN_PAUSED",
  "PAID_CAMPAIGN_RESUMED",
  "PAID_CAMPAIGN_ENDED",
  "PAID_BUDGET_REALLOCATED",
  "PAID_PROVIDER_ERROR",
  "PAID_INSIGHTS_SYNCED",
  "PAID_STOP_LOSS_TRIGGERED"
];
var PAID_DISTRIBUTION_CHECKS = [
  { linha: "SYSTEM_PAUSE = OFF", codigo: "SYSTEM_PAUSED" },
  { linha: "Paid Media category = ON", codigo: "CATEGORY_PAUSED" },
  { linha: "Meta Ads capability VERIFIED", codigo: "PAID_CAPABILITY_UNVERIFIED" },
  { linha: "ad account healthy enough for action", codigo: "PAID_AD_ACCOUNT_UNHEALTHY" },
  { linha: "audience approved", codigo: "PAID_AUDIENCE_NOT_APPROVED" },
  { linha: "Custom Audience usable if selected", codigo: "PAID_CUSTOM_AUDIENCE_UNUSABLE" },
  { linha: "content paid eligibility PASS", codigo: "PAID_CONTENT_NOT_ELIGIBLE" },
  { linha: "rights paid-media permission PASS", codigo: "PAID_RIGHTS_PERMISSION_MISSING" },
  { linha: "release/embargo PASS", codigo: "PAID_RELEASE_BLOCKED" },
  {
    linha: "Fact/Brand/Legal/Safety/Voice recheck when needed",
    codigo: "PAID_GOVERNANCE_RECHECK_REQUIRED"
  },
  { linha: "budget PASS", codigo: "PAID_BUDGET_UNAVAILABLE" },
  { linha: "objective supported", codigo: "PAID_OBJECTIVE_UNSUPPORTED" },
  { linha: "required provider fields available", codigo: "PAID_PROVIDER_FIELDS_MISSING" }
];
var PAID_DISTRIBUTION_PRECONDICAO = {
  chave: "paid_media.enabled",
  codigo: "PAID_MEDIA_DISABLED"
};
var PAID_KPIS_PRIMARIOS = [
  "Qualified Audience Reach",
  "Target Account Reach",
  "Qualified Engagement Rate",
  "Qualified Profile Visit Rate",
  "Qualified Follow Ratio",
  "CPQF / Estimated CPQF",
  "Qualified Leads",
  "Cost per Qualified Lead"
];
var PAID_KPIS_SECUNDARIOS = [
  "CPM",
  "CTR",
  "CPC",
  "frequency",
  "video retention/views when relevant"
];
var PAID_STOP_CONDITIONS_CONFIGURAVEIS = [
  { linha: "CPQF stop-loss", codigo: "PAID_STOP_CPQF_ACIMA_DO_STOP_LOSS" },
  { linha: "cost per qualified lead stop-loss", codigo: "PAID_STOP_CUSTO_POR_LEAD_QUALIFICADO" },
  { linha: "frequency ceiling", codigo: "PAID_STOP_FREQUENCIA_ACIMA_DO_TETO" },
  { linha: "minimum result threshold", codigo: "PAID_STOP_RESULTADO_MINIMO" },
  {
    linha: "maximum spend without qualified outcome",
    codigo: "PAID_STOP_GASTO_SEM_RESULTADO_QUALIFICADO"
  }
];

// src/experimentos.ts
var RESULTADOS_DE_EXPERIMENTO = [
  "WIN",
  "LOSE",
  "NO_MEANINGFUL_DIFFERENCE",
  "INSUFFICIENT_SAMPLE"
];
var COMPARACOES_DE_EXPERIMENTO_PAGO = [
  "Creative A vs B",
  "Audience A vs B",
  "Budget allocation A vs B"
];
var VARIAVEL_DA_COMPARACAO = {
  "Creative A vs B": "creative",
  "Audience A vs B": "audience",
  "Budget allocation A vs B": "budget_allocation"
};
var CAMPOS_EXIGIDOS_DO_EXPERIMENTO = [
  "hypothesis",
  "primary metric",
  "secondary metrics",
  "minimum observation window",
  "stop rule",
  "budget ceiling"
];
var PROCEDENCIAS_DE_METRICA = [
  "NATIVE_PROVIDER",
  "DIRECTLY_OBSERVED",
  "ESTIMATED_COHORT",
  "INFERRED_ATTRIBUTION"
];
var FORCAS_DE_CONCLUSAO = [
  "inconclusive",
  "confounded",
  "weak_hypothesis",
  "supported",
  "strong"
];
var TETO_DE_FORCA_POR_PROCEDENCIA = {
  NATIVE_PROVIDER: "strong",
  DIRECTLY_OBSERVED: "strong",
  ESTIMATED_COHORT: "supported",
  INFERRED_ATTRIBUTION: "weak_hypothesis"
};
function tetoDeForcaPorProcedencia(procedencias) {
  if (procedencias.length === 0) return "inconclusive";
  let indiceDoTeto = FORCAS_DE_CONCLUSAO.length - 1;
  for (const p of procedencias) {
    const i = FORCAS_DE_CONCLUSAO.indexOf(TETO_DE_FORCA_POR_PROCEDENCIA[p]);
    if (i < indiceDoTeto) indiceDoTeto = i;
  }
  return FORCAS_DE_CONCLUSAO[indiceDoTeto];
}
function aplicarTetoDeForca(forca, teto) {
  if (forca === "confounded" || teto === "confounded") return "confounded";
  const iForca = FORCAS_DE_CONCLUSAO.indexOf(forca);
  const iTeto = FORCAS_DE_CONCLUSAO.indexOf(teto);
  return iForca <= iTeto ? forca : teto;
}
export {
  ADMIN_COMMAND_TERMINAL_STATES,
  ADMIN_COMMAND_TRANSITIONS,
  AUDIENCE_APPROVAL_SCOPES,
  AUDIENCE_APPROVAL_STATUSES,
  AUDIENCE_FIELD_TYPES,
  AUDIENCE_IMPORT_FILE_TYPES,
  AUDIENCE_IMPORT_MAPPABLE_FIELDS,
  AUDIENCE_IMPORT_STATUSES,
  AUDIENCE_INTEGRATION_MODES,
  AUDIENCE_MATCHING_FIELDS,
  AUDIENCE_RECORD_KINDS,
  AUDIENCE_SEGMENT_MODES,
  AUDIENCE_SEGMENT_STATUSES,
  AUDIENCE_SOURCE_CAPABILITIES,
  AUDIENCE_SOURCE_STATUSES,
  AUDIENCE_SOURCE_TYPES,
  AUDIENCE_SYNC_RUN_STATUSES,
  AUTONOMY_LEVEL_LABELS,
  ActorRef,
  AdminCommand,
  AdminCommandRequest,
  AdminCommandStatus,
  AdminCommandType,
  AdminDenialCode,
  AgentId,
  AttentionItem,
  AudienceCohort,
  AudienceRef,
  AudienceSignal,
  AutonomyLevel,
  BACKLOG_DECISIONS,
  CAMPOS_EXIGIDOS_DO_EXPERIMENTO,
  CAPABILITY_CHANGES_NAO_MATERIAIS,
  CAPABILITY_STATUS_UNUSABLE,
  CLASSIFICATIONS_NEVER_HIDDEN,
  CLASSIFICATIONS_REPORTABLE,
  COMMAND_REQUIREMENTS,
  COMMERCIAL_STATES,
  COMMERCIAL_TRANSITIONS,
  COMMUNITY_RESPONSE_STATES,
  COMMUNITY_RESPONSE_TRANSITIONS,
  COMPARACOES_DE_EXPERIMENTO_PAGO,
  CONFIGURATION_CHANGE_STATES,
  CONFIGURATION_CHANGE_TRANSITIONS,
  CONTENT_STATES_WITH_EXTERNAL_EFFECT,
  CONTENT_TERMINAL_STATES,
  CONTENT_TRANSITIONS,
  CONTROL_EVENT_TYPES,
  CPQF_PRECISIONS,
  CampaignStage,
  CapabilityAlertSeverity,
  CapabilityChangeKind,
  CapabilityStatus,
  Claim,
  ClaimRef,
  Classification,
  CommercialEvidence,
  Company,
  CompanyRef,
  ConfigurationClass,
  ConfigurationDefinition,
  ConfigurationLevel,
  ConfigurationVersion,
  ContentFormat,
  ContentItem,
  ContentObjective,
  ContentOpportunity,
  ContentState,
  ControlEventType,
  Conversation,
  CostCategory,
  CostEnvelope,
  CostLedgerEntry,
  CreativeBrief,
  CutOpportunity,
  DECISOES_QUE_SEGURAM,
  DOMAIN_EVENT_TYPES,
  DecisionRecord,
  DeployEnvironment,
  DomainEvent,
  DomainEventType,
  ESCALATION_STATES,
  ESCALATION_TRANSITIONS,
  ESTADOS_QUE_BLOQUEIAM_ACAO_EXTERNA,
  ESTADO_DA_CAMPANHA_NO_DOCUMENTO,
  EVENT_TYPES,
  EditorialDecision,
  EscalationRecord,
  EventType,
  EvidenceLevel,
  ExceptionRecord,
  Experiment,
  FILTER_COMPOSITIONS,
  FILTER_OPERATORS,
  FILTER_OPERATORS_ORDERED,
  FILTER_OPERATORS_WITHOUT_VALUE,
  FILTER_OPERATORS_WITH_LIST_VALUE,
  FORCAS_DE_CONCLUSAO,
  FollowUpAction,
  FreshnessClass,
  FreshnessStatus,
  GateOutcome,
  GateResult,
  GateType,
  GovernanceBundle,
  GovernanceDomain,
  GovernanceResult,
  GovernanceReview,
  GovernanceReviewer,
  Guest,
  HARD_EXCLUSION_REASONS,
  Hypothesis,
  IDS_DE_VERIFICACAO,
  Interaction,
  InteractionClassification,
  KPIDefinition,
  KPISnapshot,
  KPIStatus,
  KillCriterion,
  KnowledgeEntry,
  LOYALTY_PUBLISHABLE_STATES,
  LOYALTY_STATES,
  LOYALTY_TRANSITIONS,
  Lead,
  LearningSignal,
  LogicalDomain,
  LoyaltyEpisode,
  META_AUDIENCE_BINDING_STATUSES,
  META_AUDIENCE_SYNC_MODES,
  METRIC_SNAPSHOT_SCHEDULE_HOURS,
  MODERATION_ACTIONS_REQUIRING_HUMAN,
  MODERATION_COMPOSITES_REMOVED,
  MaterialObjectEnvelope,
  MetricSnapshot,
  ModerationAction,
  ModerationCategory,
  ModerationResponse,
  ModerationVisibility,
  Money,
  NON_MATERIAL_SCHEMAS,
  OWNER_ONLY_SCOPES,
  ObjectRef,
  OverrideRecord,
  PAID_AUDIT_EVENTS,
  PAID_AUTONOMY_MODES,
  PAID_BUDGET_KINDS,
  PAID_CAMPAIGN_STATES,
  PAID_DISTRIBUTION_CHECKS,
  PAID_DISTRIBUTION_PRECONDICAO,
  PAID_GOVERNANCE_RECHECK_TRIGGERS,
  PAID_INSIGHTS_LEVELS,
  PAID_KPIS_PRIMARIOS,
  PAID_KPIS_SECUNDARIOS,
  PAID_MEDIA_PLAN_STATUSES,
  PAID_OPTIMIZATION_RECOMMENDATIONS,
  PAID_RIGHTS_PERMISSIONS,
  PAID_STOP_CONDITIONS_CONFIGURAVEIS,
  PARAMETER_CHANGE_STATES,
  PARAMETER_CHANGE_TRANSITIONS,
  PROCEDENCIAS_DE_METRICA,
  ParameterChange,
  PauseScope,
  Permission,
  PersonRef,
  PortfolioBucket,
  ProductionAsset,
  ProductionRequest,
  ProjectionWatermark,
  PublicationPackage,
  PublicationRecord,
  RESTRICTED_ACCESS_EVENT_TYPE,
  RESTRICTED_SCOPES,
  RESULTADOS_DE_EXPERIMENTO,
  RESUME_CHECK_RESULTS,
  ROLE_MAX_SCOPES,
  Recommendation,
  RelationshipEdge,
  ReleaseStatus,
  Requirement,
  ReviewIssue,
  RiskLevel,
  Role,
  SCHEMA_REGISTRY,
  SCHEMA_VERSION,
  SCOPES,
  SEEDED_HARD_EXCLUSION_POLICY,
  SERVICE_IDENTITY_MAX_SCOPES,
  SIDE_EFFECTING_EVENT_TYPES,
  SYNC_EXCLUSION_REASONS,
  SYSTEM_CONTROL_STATES,
  SYSTEM_CONTROL_TRANSITIONS,
  Scope,
  ScriptVersion,
  Severity,
  SnapshotAge,
  SocialHandle,
  SourceAsset,
  SourceReference,
  StrategicAgenda,
  TETO_DE_FORCA_POR_PROCEDENCIA,
  TimeWindow,
  TimedText,
  Timeliness,
  UNKNOWN_EXCLUSION_MARKS,
  VARIAVEL_DA_COMPARACAO,
  VERIFICACOES_DE_PRONTIDAO,
  Variant,
  aplicarTetoDeForca,
  configurationScopeFor,
  domainScopeFor,
  isFilterTree,
  isValidAdminCommandTransition,
  isValidContentTransition,
  liberaExecucao,
  materialObject,
  materialObjectFields,
  permiteAcaoExterna,
  tetoDeForcaPorProcedencia,
  vereditoDoSafeResume
};
//# sourceMappingURL=index.js.map